<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { MATERIAL_TYPE_ICONS } from '../lib/icons';
  import Logo from '../components/Logo.svelte';
  import { BRAND_NAME } from '../lib/brand';
  import { onMount } from 'svelte';
  import { replace, push } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { isExpiringSoon } from '../lib/session';
  import {
    adminState,
    textOpNeedsValue,
    type TextOp,
    type TextFilter,
  } from '../lib/admin-state';
  import type {
    ActivityMember,
    AdminCategoryDTO,
    AdminDashboardStatsDTO,
    AdminMaterialDTO,
    AdminPaymentDTO,
    AdminProductDTO,
    AdminEventDTO,
    AdminUserDTO,
    DashboardMember,
    DashboardMetric,
    DashboardPeriod,
    MaterialType,
    PopularMaterialDTO,
    RevenueDetailDTO,
    SubscriptionStatus,
    UtmReportDTO,
    UtmSelection,
  } from '../lib/types';
  import { durationLabel, formatDate } from '../lib/format';
  import { adminMode } from '../lib/admin-mode';
  import { materialDeepLink, sectionDeepLink } from '../lib/bot-links';
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import DatePicker from '../components/DatePicker.svelte';
  import UtmFilter from '../components/UtmFilter.svelte';

  type AdminTab = 'dashboard' | 'subscriptions' | 'content' | 'products' | 'schedule';
  // Seed from the cross-mount cache so navigating back from a contact
  // detail page restores the same tab.
  let activeTab = $state<AdminTab>(adminState.activeTab);
  $effect(() => {
    adminState.activeTab = activeTab;
  });

  // Скільки коментарів зі скаргами чекають на розгляд — це і є «сповіщення»
  // адміну. Тягнемо щоразу при вході в «Контент»: число мале, а застаріває
  // швидко — скарги приходять від людей, не за розкладом.
  let commentsPending = $state(0);
  $effect(() => {
    if (activeTab !== 'content') return;
    api
      .adminCommentStats()
      .then((stats) => (commentsPending = stats.reportedPending))
      .catch(() => {
        /* бейдж не критичний — мовчки лишаємо як є */
      });
  });
  const TAB_INDEX: Record<AdminTab, number> = {
    dashboard: 0,
    subscriptions: 1,
    content: 2,
    products: 3,
    schedule: 4,
  };
  const activeIndex = $derived(TAB_INDEX[activeTab]);

  // Dashboard tab state — single payload, fetched on tab entry / period change.
  let dashboardStats = $state<AdminDashboardStatsDTO | null>(null);
  let dashboardLoading = $state(false);
  let dashboardError = $state<string | null>(null);
  let dashboardPeriod = $state<DashboardPeriod>(adminState.dashboardPeriod);
  let customFrom = $state(adminState.dashboardFrom);
  let customTo = $state(adminState.dashboardTo);
  let pickYear = $state(adminState.dashboardPickYear);
  let pickMonth = $state(adminState.dashboardPickMonth);
  let pickDay = $state(adminState.dashboardPickDay); // local ISO YYYY-MM-DD
  let dashboardProduct = $state(adminState.dashboardProduct); // '' | 'unmatched' | productId
  let dashboardProducts = $state<AdminProductDTO[]>([]);
  $effect(() => {
    adminState.dashboardPeriod = dashboardPeriod;
    adminState.dashboardFrom = customFrom;
    adminState.dashboardTo = customTo;
    adminState.dashboardPickYear = pickYear;
    adminState.dashboardPickMonth = pickMonth;
    adminState.dashboardPickDay = pickDay;
    adminState.dashboardProduct = dashboardProduct;
  });

  function setProduct(): void {
    dashboardStats = null;
    fetchDashboardStats();
  }

  const PERIOD_LABEL: Record<DashboardPeriod, string> = {
    dayPick: 'Сьогодні',
    monthPick: 'Місяць',
    yearPick: 'Рік',
    all: 'Усе',
    week: 'Останні 7 днів',
    month: 'Останні 30 днів',
    custom: 'Свій період',
  };
  // Two rows: quick single-unit periods (day/month/year/all) on top, ranges below.
  const PERIODS_PRIMARY: DashboardPeriod[] = ['dayPick', 'monthPick', 'yearPick', 'all'];
  const PERIODS_SECONDARY: DashboardPeriod[] = ['week', 'month', 'custom'];
  const MONTH_NAMES = [
    'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
    'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
  ];
  // Genitive forms for «5 липня 2026» (a date reads «Nth OF month»).
  const MONTH_NAMES_GEN = [
    'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
    'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
  ];
  // Custom range needs at least one bound set to be applicable.
  const customReady = $derived(dashboardPeriod === 'custom' && (!!customFrom || !!customTo));

  const pad2 = (n: number): string => String(n).padStart(2, '0');
  function monthBounds(y: number, m: number): { from: string; to: string } {
    const lastDay = new Date(y, m + 1, 0).getDate();
    return { from: `${y}-${pad2(m + 1)}-01`, to: `${y}-${pad2(m + 1)}-${pad2(lastDay)}` };
  }
  function yearBounds(y: number): { from: string; to: string } {
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  /** Local YYYY-MM-DD for today. */
  function todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  /** «2 липня 2026» from a local ISO YYYY-MM-DD. */
  function formatFullDay(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return `${d} ${MONTH_NAMES_GEN[m - 1]} ${y}`;
  }
  function shiftMonth(delta: number): void {
    let m = pickMonth + delta;
    let y = pickYear;
    if (m < 0) { m = 11; y -= 1; }
    else if (m > 11) { m = 0; y += 1; }
    pickMonth = m;
    pickYear = y;
    dashboardStats = null;
    fetchDashboardStats();
  }
  function shiftDay(delta: number): void {
    // Parse as local date, step, reformat — avoids UTC drift from Date parsing.
    const [y, m, d] = pickDay.split('-').map(Number);
    const dt = new Date(y, m - 1, d + delta);
    pickDay = `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
    dashboardStats = null;
    fetchDashboardStats();
  }
  function shiftYear(delta: number): void {
    pickYear += delta;
    dashboardStats = null;
    fetchDashboardStats();
  }

  /** ₴ formatter from integer kopiykas. */
  function uah(cents: number): string {
    return `₴${(cents / 100).toLocaleString('uk-UA', { maximumFractionDigits: 0 })}`;
  }
  /** Compact money for chart axis: ₴1.2к / ₴350. */
  function uahK(cents: number): string {
    const v = cents / 100;
    if (v >= 1000) return `₴${(v / 1000).toLocaleString('uk-UA', { maximumFractionDigits: 1 })}к`;
    return `₴${Math.round(v)}`;
  }

  /**
   * Дельта плитки проти попереднього періоду.
   *
   * `lowerIsBetter` — для втрат і повернень: там зростання це погано, тож колір
   * інвертується (більше втрат → червоне), інакше зелений «+40% втрат» читався б
   * як успіх. `prev === 0` → відсоток не рахуємо (ділення на нуль): показуємо
   * лише напрямок і «було 0».
   */
  function delta(
    cur: number,
    prev: number | undefined,
    lowerIsBetter = false,
    fmt: (n: number) => string = String,
  ): { text: string; cls: string; title: string } | null {
    if (prev === undefined || !dashboardStats?.prev) return null;
    const range = `${formatShortDay(dashboardStats.prev.from)} – ${formatShortDay(dashboardStats.prev.to)}`;
    if (cur === prev) return { text: 'без змін', cls: 'flat', title: `так само, як ${range}` };
    const up = cur > prev;
    const good = lowerIsBetter ? !up : up;
    const pct = prev === 0 ? null : Math.round((Math.abs(cur - prev) / prev) * 1000) / 10;
    const text = pct === null ? `${up ? '▲' : '▼'} з нуля` : `${up ? '▲' : '▼'} ${pct.toLocaleString('uk-UA')}%`;
    return { text, cls: good ? 'up' : 'down', title: `${range}: ${fmt(prev)}` };
  }

  /** «5 черв.» — компактна дата для підпису порівняння. */
  function formatShortDay(iso: string): string {
    return new Date(iso).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
  }

  function setPeriod(p: DashboardPeriod): void {
    // Re-clicking «Сьогодні» jumps back to today; otherwise ignore no-op clicks.
    if (p === dashboardPeriod && p !== 'dayPick') return;
    dashboardPeriod = p;
    // Entering a stepper resets its anchor to «now» so the chip always opens on
    // today / current year (the month picker keeps its last month as before).
    if (p === 'dayPick') pickDay = todayIso();
    if (p === 'yearPick') pickYear = new Date().getFullYear();
    // For custom, wait until the admin picks a date + applies; presets and the
    // steppers reload immediately.
    if (p === 'custom') return;
    dashboardStats = null;
    fetchDashboardStats();
  }

  function applyCustomRange(): void {
    if (!customReady) return;
    dashboardStats = null;
    fetchDashboardStats();
  }

  // Resolves the UI period into the backend's (period, range). dayPick / monthPick
  // / yearPick all collapse to a custom date range (backend only knows custom).
  function currentApiPeriod(): { apiPeriod: DashboardPeriod; range?: { from?: string; to?: string } } {
    if (dashboardPeriod === 'custom')
      return { apiPeriod: 'custom', range: { from: customFrom || undefined, to: customTo || undefined } };
    if (dashboardPeriod === 'monthPick')
      return { apiPeriod: 'custom', range: monthBounds(pickYear, pickMonth) };
    if (dashboardPeriod === 'dayPick')
      return { apiPeriod: 'custom', range: { from: pickDay, to: pickDay } };
    if (dashboardPeriod === 'yearPick')
      return { apiPeriod: 'custom', range: yearBounds(pickYear) };
    return { apiPeriod: dashboardPeriod };
  }

  async function fetchDashboardStats(): Promise<void> {
    if (dashboardLoading) return;
    // Don't fire a custom request without a bound — backend would treat it as 'all'.
    if (dashboardPeriod === 'custom' && !customReady) return;
    const { apiPeriod, range } = currentApiPeriod();
    dashboardLoading = true;
    dashboardError = null;
    try {
      dashboardStats = await api.adminDashboardStats(apiPeriod, range, dashboardProduct || undefined);
    } catch {
      dashboardError = 'Не вдалось завантажити статистику';
    } finally {
      dashboardLoading = false;
    }
    // Звіт по мітках — окремим запитом, щоб перемикання виміру не перетягувало
    // весь дашборд. Періодом і продуктом керує та сама панель зверху.
    void loadUtmReport();
  }

  // ── Tile drill-in: contacts behind a metric, same period + product filter ──
  let membersMetric = $state<DashboardMetric | null>(null);
  let membersTitle = $state('');
  let membersItems = $state<DashboardMember[]>([]);
  let membersTotal = $state(0);
  let membersPage = $state(1);
  let membersHasMore = $state(false);
  let membersLoading = $state(false);
  let membersError = $state<string | null>(null);

  async function loadMembers(page: number): Promise<void> {
    if (!membersMetric) return;
    membersLoading = true;
    membersError = null;
    try {
      const { apiPeriod, range } = currentApiPeriod();
      const res = await api.adminDashboardMembers(
        membersMetric,
        apiPeriod,
        range,
        dashboardProduct || undefined,
        page,
      );
      membersItems = page === 1 ? res.items : [...membersItems, ...res.items];
      membersTotal = res.total;
      membersPage = res.page;
      membersHasMore = res.hasMore;
    } catch {
      membersError = 'Не вдалось завантажити контакти';
    } finally {
      membersLoading = false;
    }
  }

  function openMembers(metric: DashboardMetric, title: string): void {
    membersMetric = metric;
    membersTitle = title;
    membersItems = [];
    membersTotal = 0;
    membersPage = 1;
    membersHasMore = false;
    adminState.dashboardModal = { kind: 'members', metric, title };
    loadMembers(1);
  }

  function closeMembers(): void {
    membersMetric = null;
    adminState.dashboardModal = null;
  }

  // Navigate to a contact — keep adminState.dashboardModal so the drill-in
  // reopens when the user comes back (the page unmounts, hiding the modal).
  function openContact(telegramId: string): void {
    push(`/admin/user/${telegramId}`);
  }

  // ── Payment-tile drill-in: individual payments, same period + product ──
  let payStatus = $state<'succeeded' | 'refunded' | null>(null);
  let payTitle = $state('');
  let payItems = $state<AdminPaymentDTO[]>([]);
  let payTotal = $state(0);
  let payPage = $state(1);
  let payHasMore = $state(false);
  let payLoading = $state(false);
  let payError = $state<string | null>(null);
  // Drill-in context when opened from a tariff / source block in the product
  // modal. payProductId overrides the global product scope; payReturnTariffs
  // remembers which product modal to reopen on close.
  let payProductId = $state<string | null>(null);
  let payTariffId = $state<string | null>(null);
  let paySource = $state<string | null>(null);
  let payUtmDim = $state<'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm' | null>(null);
  let payUtmValue = $state<string | null>(null);
  let payReturnTariffs = $state<{ productId: string; title: string } | null>(null);

  async function loadPayments(page: number): Promise<void> {
    if (!payStatus) return;
    payLoading = true;
    payError = null;
    try {
      const { apiPeriod, range } = currentApiPeriod();
      const res = await api.adminDashboardPayments(
        payStatus,
        apiPeriod,
        range,
        payProductId ?? (dashboardProduct || undefined),
        page,
        {
          tariffId: payTariffId ?? undefined,
          source: paySource ?? undefined,
          utmDim: payUtmDim ?? undefined,
          utmValue: payUtmValue ?? undefined,
        },
      );
      payItems = page === 1 ? res.items : [...payItems, ...res.items];
      payTotal = res.total;
      payPage = res.page;
      payHasMore = res.hasMore;
    } catch {
      payError = 'Не вдалось завантажити оплати';
    } finally {
      payLoading = false;
    }
  }

  function openPayments(
    status: 'succeeded' | 'refunded',
    title: string,
    opts?: {
      productId?: string;
      tariffId?: string;
      source?: string;
      utmDim?: 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm';
      utmValue?: string;
    },
  ): void {
    payStatus = status;
    payTitle = title;
    payItems = [];
    payTotal = 0;
    payPage = 1;
    payHasMore = false;
    payProductId = opts?.productId ?? null;
    payTariffId = opts?.tariffId ?? null;
    paySource = opts?.source ?? null;
    payUtmDim = opts?.utmDim ?? null;
    payUtmValue = opts?.utmValue ?? null;
    adminState.dashboardModal = {
      kind: 'payments',
      status,
      title,
      productId: opts?.productId,
      tariffId: opts?.tariffId,
      source: opts?.source,
      utmDim: opts?.utmDim,
      utmValue: opts?.utmValue,
    };
    loadPayments(1);
  }

  // Open the payments behind one tariff block of the product modal.
  function openTariffPayments(tariffId: string | null, tariffTitle: string): void {
    if (!tariffModalProductId) return;
    const prodId = tariffModalProductId;
    payReturnTariffs = { productId: prodId, title: tariffModalTitle };
    const title = `${tariffModalTitle} · ${tariffTitle}`;
    closeTariffs();
    openPayments('succeeded', title, { productId: prodId, tariffId: tariffId ?? '__none__' });
  }
  // Open the payments behind one source block of the product modal.
  function openSourcePayments(source: string | null, sourceTitle: string): void {
    if (!tariffModalProductId) return;
    const prodId = tariffModalProductId;
    payReturnTariffs = { productId: prodId, title: tariffModalTitle };
    const title = `${tariffModalTitle} · ${sourceTitle}`;
    closeTariffs();
    openPayments('succeeded', title, { productId: prodId, source: source ?? '__none__' });
  }
  // Те саме, але по utm_source — окрема вісь від воронки.
  function openUtmSourcePayments(value: string | null, valueTitle: string): void {
    if (!tariffModalProductId) return;
    const prodId = tariffModalProductId;
    payReturnTariffs = { productId: prodId, title: tariffModalTitle };
    const title = `${tariffModalTitle} · ${valueTitle}`;
    closeTariffs();
    openPayments('succeeded', title, {
      productId: prodId,
      utmDim: 'utmSource',
      utmValue: value ?? '__none__',
    });
  }

  function closePayments(): void {
    payStatus = null;
    // Returning from a tariff/source drill-in reopens the product modal.
    if (payReturnTariffs) {
      const r = payReturnTariffs;
      payReturnTariffs = null;
      openTariffs(r.productId, r.title);
    } else {
      adminState.dashboardModal = null;
    }
  }

  function openPayment(id: string): void {
    push(`/admin/payment/${id}`);
  }

  // Script-side twin of the template's `periodWord` @const.
  function periodWordText(): string {
    if (dashboardPeriod === 'all') return 'за весь час';
    if (dashboardPeriod === 'custom') return 'за період';
    if (dashboardPeriod === 'monthPick') return `за ${MONTH_NAMES[pickMonth].toLowerCase()} ${pickYear}`;
    if (dashboardPeriod === 'dayPick') return `за ${formatFullDay(pickDay)}`;
    if (dashboardPeriod === 'yearPick') return `за ${pickYear}`;
    return `за ${PERIOD_LABEL[dashboardPeriod].toLowerCase()}`;
  }

  // ── Revenue detail drill-in (Виручка брутто / Середній чек) ──
  let revenueModal = $state<'gross' | 'avg' | null>(null);
  let revenueDetail = $state<RevenueDetailDTO | null>(null);
  let revenueLoading = $state(false);
  let revenueError = $state<string | null>(null);
  let revBarSel = $state<number | null>(null); // selected bucket index in the trend chart
  const maxSeries = $derived(
    revenueDetail ? Math.max(1, ...revenueDetail.series.map((d) => d.gross)) : 1,
  );
  // Consecutive month buckets grouped by year → a year axis under the month labels.
  const revYearGroups = $derived.by(() => {
    const dd = revenueDetail;
    if (!dd || dd.granularity !== 'month') return [] as { year: number; span: number }[];
    const groups: { year: number; span: number }[] = [];
    for (const s of dd.series) {
      const y = s.year ?? 0;
      const last = groups[groups.length - 1];
      if (last && last.year === y) last.span += 1;
      else groups.push({ year: y, span: 1 });
    }
    return groups;
  });
  const maxHist = $derived(
    revenueDetail ? Math.max(1, ...revenueDetail.histogram.map((h) => h.count)) : 1,
  );
  const revenueTitle = $derived(
    revenueModal === 'gross' ? `Виручка ${periodWordText()}` : `Середній чек ${periodWordText()}`,
  );

  async function openRevenueDetail(kind: 'gross' | 'avg'): Promise<void> {
    revenueModal = kind;
    revenueError = null;
    if (!revenueDetail && !revenueLoading) {
      revenueLoading = true;
      try {
        const { apiPeriod, range } = currentApiPeriod();
        revenueDetail = await api.adminRevenueDetail(apiPeriod, range, dashboardProduct || undefined);
      } catch {
        revenueError = 'Не вдалось завантажити';
      } finally {
        revenueLoading = false;
      }
    }
  }
  function closeRevenue(): void {
    revenueModal = null;
    revBarSel = null;
  }
  // Reset cached detail when the period/product changes so a reopen refetches.
  $effect(() => {
    void dashboardPeriod;
    void dashboardProduct;
    void customFrom;
    void customTo;
    void pickDay;
    void pickMonth;
    void pickYear;
    revenueDetail = null;
    revBarSel = null;
  });

  // ── «Популярні матеріали» full-list drill-in ──
  let popularOpen = $state(false);
  let popularItems = $state<PopularMaterialDTO[]>([]);
  let popularTotal = $state(0);
  let popularPage = $state(1);
  let popularHasMore = $state(false);
  let popularLoading = $state(false);
  let popularError = $state<string | null>(null);

  async function loadPopular(page: number): Promise<void> {
    popularLoading = true;
    popularError = null;
    try {
      const res = await api.adminPopularMaterials(page);
      popularItems = page === 1 ? res.items : [...popularItems, ...res.items];
      popularTotal = res.total;
      popularPage = res.page;
      popularHasMore = res.hasMore;
    } catch {
      popularError = 'Не вдалось завантажити список';
    } finally {
      popularLoading = false;
    }
  }

  function openPopular(): void {
    popularOpen = true;
    popularItems = [];
    popularTotal = 0;
    popularPage = 1;
    popularHasMore = false;
    adminState.dashboardModal = { kind: 'popular' };
    loadPopular(1);
  }

  function closePopular(): void {
    popularOpen = false;
    adminState.dashboardModal = null;
  }

  function openMaterial(m: PopularMaterialDTO): void {
    push(m.category ? `/material/${m.id}` : `/admin/material/${m.id}/edit`);
  }

  // ── «Активність по днях» drill-in: people active on one day ──
  let activityDate = $state<string | null>(null);
  let activityTitle = $state('');
  let activityItems = $state<ActivityMember[]>([]);
  let activityTotal = $state(0);
  let activityPage = $state(1);
  let activityHasMore = $state(false);
  let activityLoading = $state(false);
  let activityError = $state<string | null>(null);

  async function loadActivity(page: number): Promise<void> {
    if (!activityDate) return;
    activityLoading = true;
    activityError = null;
    try {
      const res = await api.adminDashboardActivity(activityDate, page);
      activityItems = page === 1 ? res.items : [...activityItems, ...res.items];
      activityTotal = res.total;
      activityPage = res.page;
      activityHasMore = res.hasMore;
    } catch {
      activityError = 'Не вдалось завантажити список';
    } finally {
      activityLoading = false;
    }
  }

  function openActivity(date: string, weekday: string): void {
    activityDate = date;
    const [y, m, d] = date.split('-');
    activityTitle = `Активність · ${weekday} ${d}.${m}.${y}`;
    activityItems = [];
    activityTotal = 0;
    activityPage = 1;
    activityHasMore = false;
    adminState.dashboardModal = { kind: 'activity', date, weekday };
    loadActivity(1);
  }

  function closeActivity(): void {
    activityDate = null;
    adminState.dashboardModal = null;
  }

  // ── Розділ «UTM» на дашборді ───────────────────────────────────────────────
  // Окремий запит (не частина stats), бо вимір перемикається без перезавантаження
  // решти дашборда.
  const UTM_DIM_TABS: { key: UtmReportDTO['dim']; label: string }[] = [
    { key: 'source', label: 'source' },
    { key: 'medium', label: 'medium' },
    { key: 'campaign', label: 'campaign' },
    { key: 'content', label: 'content' },
    { key: 'term', label: 'term' },
  ];
  /** Вимір звіту → поле фільтра оплат для drill-in. */
  const UTM_DIM_TO_FIELD: Record<UtmReportDTO['dim'], 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm'> = {
    source: 'utmSource',
    medium: 'utmMedium',
    campaign: 'utmCampaign',
    content: 'utmContent',
    term: 'utmTerm',
  };
  let utmDim = $state<UtmReportDTO['dim']>(adminState.dashboardUtmDim);
  let utmReport = $state<UtmReportDTO | null>(null);
  let utmLoading = $state(false);
  let utmError = $state<string | null>(null);
  $effect(() => {
    adminState.dashboardUtmDim = utmDim;
  });

  async function loadUtmReport(): Promise<void> {
    utmLoading = true;
    utmError = null;
    try {
      const { apiPeriod, range } = currentApiPeriod();
      utmReport = await api.adminUtmReport(utmDim, apiPeriod, range, dashboardProduct || undefined);
    } catch {
      utmError = 'Не вдалось завантажити звіт по мітках';
      utmReport = null;
    } finally {
      utmLoading = false;
    }
  }

  function setUtmDim(dim: UtmReportDTO['dim']): void {
    if (utmDim === dim) return;
    utmDim = dim;
    void loadUtmReport();
  }

  /** Оплати за одним значенням мітки — той самий модальний список платежів. */
  function openUtmPayments(row: { value: string | null; title: string }): void {
    openPayments('succeeded', `UTM ${utmDim} · ${row.title}`, {
      productId: dashboardProduct || undefined,
      utmDim: UTM_DIM_TO_FIELD[utmDim],
      utmValue: row.value ?? '__none__',
    });
  }

  // ── «Виручка по продуктах» → tariff breakdown modal (data reused from stats) ──
  let tariffModalProductId = $state<string | null>(null);
  let tariffModalTitle = $state('');
  const tariffModalRow = $derived(
    dashboardStats?.revenueByProduct.find((r) => r.productId === tariffModalProductId) ?? null,
  );

  function openTariffs(productId: string, title: string): void {
    tariffModalProductId = productId;
    tariffModalTitle = title;
    adminState.dashboardModal = { kind: 'tariffs', productId, title };
  }
  function closeTariffs(): void {
    tariffModalProductId = null;
    adminState.dashboardModal = null;
  }

  // Reopen the drill-in modal that was open before navigating to a detail page,
  // so «Назад» lands back on the dashboard with the same list showing.
  function restoreDashboardModal(): void {
    const m = adminState.dashboardModal;
    if (!m) return;
    if (m.kind === 'members') openMembers(m.metric, m.title);
    else if (m.kind === 'payments')
      openPayments(m.status, m.title, {
        productId: m.productId,
        tariffId: m.tariffId,
        source: m.source,
        utmDim: m.utmDim,
        utmValue: m.utmValue,
      });
    else if (m.kind === 'popular') openPopular();
    else if (m.kind === 'activity') openActivity(m.date, m.weekday);
    else if (m.kind === 'tariffs') openTariffs(m.productId, m.title);
  }

  // Subscriptions tab state
  let users = $state<AdminUserDTO[] | null>(null);
  let total = $state(0);
  let hasMore = $state(false);
  let page = $state(1);
  let loading = $state(false);
  let loadingMore = $state(false);
  // All Subscriptions filter state is seeded from the cross-mount cache so
  // navigating to a contact detail page and back preserves search + filters.
  let searchInput = $state(adminState.subsSearch);
  let statusFilter = $state<SubscriptionStatus[]>(adminState.subsStatusFilter);
  let tariffFilter = $state<string[]>(adminState.subsTariffFilter);
  type DateOp = 'before' | 'after' | 'on' | 'between';
  type DateFilter = { op: DateOp; date1: string; date2?: string };
  let startFilter = $state<DateFilter | null>(adminState.subsStartFilter);
  let endFilter = $state<DateFilter | null>(adminState.subsEndFilter);
  let emailFilter = $state<TextFilter | null>(adminState.subsEmailFilter);
  let phoneFilter = $state<TextFilter | null>(adminState.subsPhoneFilter);
  let usernameFilter = $state<TextFilter | null>(adminState.subsUsernameFilter);
  let utmFilter = $state<UtmSelection>(adminState.subsUtmFilter);
  $effect(() => {
    adminState.subsSearch = searchInput;
  });
  $effect(() => {
    adminState.subsUtmFilter = utmFilter;
  });
  $effect(() => {
    adminState.subsStatusFilter = statusFilter;
  });
  $effect(() => {
    adminState.subsTariffFilter = tariffFilter;
  });
  $effect(() => {
    adminState.subsStartFilter = startFilter;
  });
  $effect(() => {
    adminState.subsEndFilter = endFilter;
  });
  $effect(() => {
    adminState.subsEmailFilter = emailFilter;
  });
  $effect(() => {
    adminState.subsPhoneFilter = phoneFilter;
  });
  $effect(() => {
    adminState.subsUsernameFilter = usernameFilter;
  });
  let error = $state<string | null>(null);
  // Subscriptions filter modal — draft state edited inside the sheet.
  let subsFilterModalOpen = $state(false);
  let draftStatuses = $state<SubscriptionStatus[]>([]);
  let draftTariffs = $state<string[]>([]);
  let draftStartOp = $state<'' | DateOp>('');
  let draftStartDate1 = $state('');
  let draftStartDate2 = $state('');
  let draftEndOp = $state<'' | DateOp>('');
  let draftEndDate1 = $state('');
  let draftEndDate2 = $state('');
  let draftEmailOp = $state<'' | TextOp>('');
  let draftEmailValue = $state('');
  let draftPhoneOp = $state<'' | TextOp>('');
  let draftPhoneValue = $state('');
  let draftUsernameOp = $state<'' | TextOp>('');
  let draftUsernameValue = $state('');
  let draftUtm = $state<UtmSelection>({});
  let availableTariffs = $state<string[] | null>(null);

  /** Скільки значень обрано в UTM-виборі загалом (по всіх вимірах). */
  function utmCount(sel: UtmSelection): number {
    return Object.values(sel).reduce((n, vals) => n + (vals?.length ?? 0), 0);
  }
  // Sub-tab inside Content tab — switch between editing Розділи and Матеріали.
  let contentSubTab = $state<'categories' | 'materials'>(adminState.contentSubTab);
  $effect(() => {
    adminState.contentSubTab = contentSubTab;
  });

  // Copy-link affordance on material/section rows. `copiedLinkId` drives a
  // brief check mark on the button (material or section) that was just clicked.
  let copiedLinkId = $state<string | null>(null);
  let copiedTimer: ReturnType<typeof setTimeout> | null = null;
  async function copyDeepLink(e: MouseEvent, id: string, link: string): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard API needs a secure context; fall back to the legacy path so
      // copy still works over plain http (local dev, older Telegram WebViews).
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* nothing else we can do — leave the link uncopied */
      }
      document.body.removeChild(ta);
    }
    copiedLinkId = id;
    if (copiedTimer) clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => (copiedLinkId = null), 1500);
  }

  const TEXT_OP_LABELS: Record<TextOp, string> = {
    empty: 'пуста',
    notEmpty: 'не пуста',
    contains: 'містить',
    notContains: 'не містить',
    equals: 'дорівнює',
    notEquals: 'не дорівнює',
  };

  const DATE_OP_LABELS: Record<DateOp, string> = {
    before: 'до',
    after: 'після',
    on: 'дорівнює',
    between: 'між',
  };

  const subsActiveFiltersCount = $derived(
    statusFilter.length +
      tariffFilter.length +
      (startFilter ? 1 : 0) +
      (endFilter ? 1 : 0) +
      (emailFilter ? 1 : 0) +
      (phoneFilter ? 1 : 0) +
      (usernameFilter ? 1 : 0) +
      utmCount(utmFilter),
  );

  function textFilterLabel(f: TextFilter): string {
    return f.value ? `${TEXT_OP_LABELS[f.op]} «${f.value}»` : TEXT_OP_LABELS[f.op];
  }

  function formatIsoDay(iso: string): string {
    const [datePart, timePart] = iso.split('T');
    const [y, m, d] = datePart.split('-');
    return `${d}.${m}.${y}${timePart ? ` ${timePart}` : ''}`;
  }
  function dateFilterLabel(f: DateFilter): string {
    if (f.op === 'between' && f.date2) {
      return `${DATE_OP_LABELS.between} ${formatIsoDay(f.date1)} і ${formatIsoDay(f.date2)}`;
    }
    return `${DATE_OP_LABELS[f.op]} ${formatIsoDay(f.date1)}`;
  }

  // Status badge mapping
  const STATUS_LABEL: Record<SubscriptionStatus, string> = {
    active: 'Активна',
    inactive: 'Закінчилась',
    none: 'Без підписки',
  };
  const STATUS_CLASS: Record<SubscriptionStatus, string> = {
    active: 'badge-active',
    inactive: 'badge-expired',
    none: 'badge-muted',
  };

  function initials(u: AdminUserDTO): string {
    const a = u.name?.charAt(0) ?? '';
    const b = u.lastName?.charAt(0) ?? '';
    return (a + b).toUpperCase() || '?';
  }

  function formatDayMonth(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  /** Fetch subscriptions — replace list (loading) or append (loadingMore). */
  async function fetchUsers(opts: { append?: boolean } = {}): Promise<void> {
    if (opts.append) loadingMore = true;
    else loading = true;
    error = null;
    try {
      const res = await api.adminUsers({
        search: searchInput.trim() || undefined,
        statuses: statusFilter.length > 0 ? statusFilter : undefined,
        tariffs: tariffFilter.length > 0 ? tariffFilter : undefined,
        startOp: startFilter?.op,
        startDate1: startFilter?.date1,
        startDate2: startFilter?.date2,
        endOp: endFilter?.op,
        endDate1: endFilter?.date1,
        endDate2: endFilter?.date2,
        emailOp: emailFilter?.op,
        emailValue: emailFilter?.value,
        phoneOp: phoneFilter?.op,
        phoneValue: phoneFilter?.value,
        usernameOp: usernameFilter?.op,
        usernameValue: usernameFilter?.value,
        utm: utmCount(utmFilter) ? utmFilter : undefined,
        page: opts.append ? page + 1 : 1,
        limit: 20,
      });
      if (opts.append) {
        users = [...(users ?? []), ...res.items];
        page = res.page;
      } else {
        users = res.items;
        page = 1;
      }
      total = res.total;
      hasMore = res.hasMore;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        replace('/home');
        return;
      }
      error = 'Не вдалось завантажити список';
    } finally {
      loading = false;
      loadingMore = false;
    }
  }

  // Debounce search input — 300ms to avoid hammering the API on each keystroke.
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function onSearchChange(e: Event): void {
    searchInput = (e.target as HTMLInputElement).value;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => fetchUsers(), 300);
  }
  function openSubsFilterModal(): void {
    draftStatuses = [...statusFilter];
    draftTariffs = [...tariffFilter];
    draftStartOp = startFilter?.op ?? '';
    draftStartDate1 = startFilter?.date1 ?? '';
    draftStartDate2 = startFilter?.date2 ?? '';
    draftEndOp = endFilter?.op ?? '';
    draftEndDate1 = endFilter?.date1 ?? '';
    draftEndDate2 = endFilter?.date2 ?? '';
    draftEmailOp = emailFilter?.op ?? '';
    draftEmailValue = emailFilter?.value ?? '';
    draftPhoneOp = phoneFilter?.op ?? '';
    draftPhoneValue = phoneFilter?.value ?? '';
    draftUsernameOp = usernameFilter?.op ?? '';
    draftUsernameValue = usernameFilter?.value ?? '';
    draftUtm = { ...utmFilter };
    subsFilterModalOpen = true;
    // Refresh the tariff list every time the modal opens so a brand-new
    // tariff (added in SP after the page loaded) shows up immediately.
    // First open shows a loading placeholder; subsequent opens keep the
    // previous list visible until the new one arrives — no flicker.
    api
      .adminTariffs()
      .then((r) => (availableTariffs = r.items))
      .catch(() => {
        if (availableTariffs === null) availableTariffs = [];
      });
  }
  function closeSubsFilterModal(): void {
    subsFilterModalOpen = false;
  }
  /** Build a DateFilter from the draft state, or null if op/date are missing. */
  function buildDateFilter(op: '' | DateOp, d1: string, d2: string): DateFilter | null {
    if (!op || !d1) return null;
    if (op === 'between' && !d2) return null;
    return op === 'between' ? { op, date1: d1, date2: d2 } : { op, date1: d1 };
  }
  function buildTextFilter(op: '' | TextOp, value: string): TextFilter | null {
    if (!op) return null;
    if (textOpNeedsValue(op) && !value.trim()) return null;
    return textOpNeedsValue(op) ? { op, value: value.trim() } : { op };
  }
  function applySubsFilters(): void {
    statusFilter = draftStatuses;
    tariffFilter = draftTariffs;
    startFilter = buildDateFilter(draftStartOp, draftStartDate1, draftStartDate2);
    endFilter = buildDateFilter(draftEndOp, draftEndDate1, draftEndDate2);
    emailFilter = buildTextFilter(draftEmailOp, draftEmailValue);
    phoneFilter = buildTextFilter(draftPhoneOp, draftPhoneValue);
    usernameFilter = buildTextFilter(draftUsernameOp, draftUsernameValue);
    utmFilter = { ...draftUtm };
    subsFilterModalOpen = false;
    fetchUsers();
  }
  function resetSubsDraftFilters(): void {
    draftStatuses = [];
    draftTariffs = [];
    draftStartOp = '';
    draftStartDate1 = '';
    draftStartDate2 = '';
    draftEndOp = '';
    draftEndDate1 = '';
    draftEndDate2 = '';
    draftEmailOp = '';
    draftEmailValue = '';
    draftPhoneOp = '';
    draftPhoneValue = '';
    draftUsernameOp = '';
    draftUsernameValue = '';
    draftUtm = {};
  }
  function toggleDraftStatus(s: SubscriptionStatus): void {
    draftStatuses = draftStatuses.includes(s)
      ? draftStatuses.filter((x) => x !== s)
      : [...draftStatuses, s];
  }
  function toggleDraftTariff(t: string): void {
    draftTariffs = draftTariffs.includes(t)
      ? draftTariffs.filter((x) => x !== t)
      : [...draftTariffs, t];
  }
  function removeStatusFilter(s: SubscriptionStatus): void {
    statusFilter = statusFilter.filter((x) => x !== s);
    fetchUsers();
  }
  function removeTariffFilter(t: string): void {
    tariffFilter = tariffFilter.filter((x) => x !== t);
    fetchUsers();
  }
  function removeStartFilter(): void {
    startFilter = null;
    fetchUsers();
  }
  function removeEndFilter(): void {
    endFilter = null;
    fetchUsers();
  }
  function removeEmailFilter(): void {
    emailFilter = null;
    fetchUsers();
  }
  function removePhoneFilter(): void {
    phoneFilter = null;
    fetchUsers();
  }
  function removeUsernameFilter(): void {
    usernameFilter = null;
    fetchUsers();
  }

  // Trigger fetch when entering the Subscriptions tab for the first time.
  $effect(() => {
    if (activeTab === 'subscriptions' && users === null && !loading) {
      fetchUsers();
    }
  });

  // Trigger fetch when entering the Dashboard tab for the first time.
  $effect(() => {
    if (activeTab === 'dashboard' && dashboardStats === null && !dashboardLoading) {
      fetchDashboardStats();
    }
    // Load product list once for the product filter.
    if (activeTab === 'dashboard' && dashboardProducts.length === 0) {
      api.adminProducts().then((r) => (dashboardProducts = r.items)).catch(() => {});
    }
  });

  /* ============================================================
     Content tab state
     ============================================================ */
  let materials = $state<AdminMaterialDTO[] | null>(null);
  let matTotal = $state(0);
  let matHasMore = $state(false);
  let matPage = $state(1);
  let matLoading = $state(false);
  let matLoadingMore = $state(false);
  let matError = $state<string | null>(null);
  let matSearchInput = $state('');
  // Active filters — applied to the list. The Filter modal mutates drafts and
  // commits them here on «Застосувати», so the list doesn't re-fetch as the
  // user fiddles with checkboxes inside the modal.
  let matTypeFilter = $state<'all' | MaterialType>('all');
  let matCategoryFilter = $state<string[]>([]);
  let matPublishFilter = $state<'all' | 'published' | 'draft'>('all');
  // Categories sub-tab filters (client-side, list is small).
  let catSearchInput = $state('');
  let catPublishFilter = $state<'all' | 'published' | 'draft'>('all');
  let catFilterModalOpen = $state(false);
  let draftCatPublish = $state<'all' | 'published' | 'draft'>('all');
  const catActiveFiltersCount = $derived(catPublishFilter !== 'all' ? 1 : 0);
  function openCatFilterModal(): void {
    draftCatPublish = catPublishFilter;
    catFilterModalOpen = true;
  }
  function closeCatFilterModal(): void {
    catFilterModalOpen = false;
  }
  function applyCatFilters(): void {
    catPublishFilter = draftCatPublish;
    catFilterModalOpen = false;
  }
  function resetCatDraftFilters(): void {
    draftCatPublish = 'all';
  }
  function removeCatPublishFilter(): void {
    catPublishFilter = 'all';
  }
  // Draft state used inside the Filter modal.
  let filterModalOpen = $state(false);
  let draftType = $state<'all' | MaterialType>('all');
  let draftCategoryIds = $state<string[]>([]);
  let draftPublish = $state<'all' | 'published' | 'draft'>('all');
  let togglingId = $state<string | null>(null);
  let categories = $state<AdminCategoryDTO[] | null>(null);
  let catsExpanded = $state(false);
  const activeFiltersCount = $derived(
    (matTypeFilter !== 'all' ? 1 : 0) +
      matCategoryFilter.length +
      (matPublishFilter !== 'all' ? 1 : 0),
  );
  const PUBLISH_LABEL: Record<'published' | 'draft', string> = {
    published: 'Опубліковано',
    draft: 'Чернетка',
  };

  // Client-side filtered list of categories for the Розділи sub-tab.
  const visibleCategories = $derived.by(() => {
    if (!categories) return null;
    const q = catSearchInput.trim().toLowerCase();
    return categories.filter((c) => {
      if (q && !c.name.toLowerCase().includes(q)) return false;
      if (catPublishFilter === 'published' && !c.isActive) return false;
      if (catPublishFilter === 'draft' && c.isActive) return false;
      return true;
    });
  });
  // === Reorder mode ===
  // Admin enters via the ↕ button next to Filter. While on, drag handles
  // are visible on every card, cards don't navigate on tap, edit buttons
  // disappear. Exit triggers a confirmation modal if the order changed,
  // letting the admin save or revert. Nothing is persisted until they
  // explicitly confirm — drags are local-only inside the mode.
  let reorderModeOn = $state(false);
  let originalOrder = $state<AdminCategoryDTO[] | null>(null);
  let confirmExitOpen = $state(false);
  let savingReorder = $state(false);

  const reorderDirty = $derived.by(() => {
    if (!reorderModeOn || !originalOrder || !categories) return false;
    if (categories.length !== originalOrder.length) return true;
    return categories.some((c, i) => c.id !== originalOrder![i].id);
  });

  function enterReorderMode(): void {
    if (!categories) return;
    originalOrder = [...categories];
    catSearchInput = '';
    catPublishFilter = 'all';
    reorderModeOn = true;
  }

  function requestExitReorder(): void {
    if (reorderDirty) {
      confirmExitOpen = true;
    } else {
      reorderModeOn = false;
      originalOrder = null;
    }
  }

  async function confirmSaveReorder(): Promise<void> {
    if (!categories || savingReorder) return;
    savingReorder = true;
    try {
      await api.adminReorderCategories(categories.map((c) => c.id));
      reorderModeOn = false;
      originalOrder = null;
      confirmExitOpen = false;
    } catch {
      /* TODO: surface inline error; for now leave mode on so admin can retry */
    } finally {
      savingReorder = false;
    }
  }

  function revertReorder(): void {
    if (originalOrder) categories = originalOrder;
    reorderModeOn = false;
    originalOrder = null;
    confirmExitOpen = false;
  }

  function onDndConsider(e: CustomEvent<DndEvent<AdminCategoryDTO>>): void {
    categories = e.detail.items;
  }
  function onDndFinalize(e: CustomEvent<DndEvent<AdminCategoryDTO>>): void {
    categories = e.detail.items;
  }

  // === Products tab ===
  // Mirrors the Розділи sub-tab UX: search + status/scope filters + drag
  // reorder + create button. Reorder here is purely cosmetic (admin list order
  // only — no effect on the catalog or access), so it persists on each drop
  // without a confirm step.
  let products = $state<AdminProductDTO[] | null>(null);
  let productsSearch = $state(adminState.productsSearch);
  let productsScopeFilter = $state<'all' | 'selected' | null>(adminState.productsScopeFilter);
  let productsReorderOn = $state(false);
  $effect(() => {
    adminState.productsSearch = productsSearch;
    adminState.productsScopeFilter = productsScopeFilter;
  });

  async function fetchProducts(): Promise<void> {
    try {
      const res = await api.adminProducts();
      products = res.items;
    } catch {
      products = [];
    }
  }

  // ── Schedule tab: events list ──
  let events = $state<AdminEventDTO[] | null>(null);
  async function fetchEvents(): Promise<void> {
    try {
      const res = await api.adminEvents();
      events = res.items;
    } catch {
      events = [];
    }
  }
  function eventWhen(iso: string): string {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  }
  function hhmm(iso: string): string {
    const d = new Date(iso);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  /** «2026-06-04» → «4.06» for the activity chart labels. */
  function dayMonth(iso: string): string {
    const [, m, d] = iso.split('-');
    return `${Number(d)}.${m}`;
  }

  // Schedule tab: list ↔ calendar view.
  const SCHED_WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
  let scheduleView = $state<'list' | 'calendar'>('list');
  let calMonth = $state(`${new Date().getFullYear()}-${pad2(new Date().getMonth() + 1)}`);
  let calSelectedDay = $state<string | null>(null);
  function shiftCalMonth(delta: number): void {
    const [y, m] = calMonth.split('-').map(Number);
    const dt = new Date(y, m - 1 + delta, 1);
    calMonth = `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}`;
    calSelectedDay = null;
  }
  const eventsByDay = $derived.by(() => {
    const map = new Map<string, AdminEventDTO[]>();
    for (const ev of events ?? []) {
      const d = new Date(ev.startsAt);
      const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    // Sort each day's events by time ascending.
    for (const list of map.values()) list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    return map;
  });
  const calGrid = $derived.by(() => {
    const [y, m] = calMonth.split('-').map(Number);
    const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    const lead = (firstDow + 6) % 7; // Monday-first
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const cells: ({ day: number; date: string; count: number } | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      const date = `${calMonth}-${pad2(d)}`;
      cells.push({ day: d, date, count: eventsByDay.get(date)?.length ?? 0 });
    }
    return cells;
  });
  const calMonthLabel = $derived.by(() => {
    const [y, m] = calMonth.split('-').map(Number);
    return `${MONTH_NAMES[m - 1]} ${y}`;
  });
  const selectedDayEvents = $derived(calSelectedDay ? eventsByDay.get(calSelectedDay) ?? [] : []);

  const visibleProducts = $derived.by(() => {
    if (!products) return null;
    const q = productsSearch.trim().toLowerCase();
    return products.filter((p) => {
      if (q && !p.title.toLowerCase().includes(q) && !(p.description ?? '').toLowerCase().includes(q))
        return false;
      if (productsScopeFilter && p.accessScope !== productsScopeFilter) return false;
      return true;
    });
  });

  function onProductsDndConsider(e: CustomEvent<DndEvent<AdminProductDTO>>): void {
    products = e.detail.items;
  }
  async function onProductsDndFinalize(e: CustomEvent<DndEvent<AdminProductDTO>>): Promise<void> {
    products = e.detail.items;
    try {
      await api.adminReorderProducts(e.detail.items.map((p) => p.id));
    } catch {
      /* cosmetic order only — a failed save is non-critical; refetch on reopen */
    }
  }

  function scopeLabel(scope: 'all' | 'selected'): string {
    return scope === 'all' ? `${BRAND_NAME} · усі групи` : 'Міні-курс';
  }

  // === Materials reorder mode ===
  // Two-step flow: toggle on → pick a category → reorder its materials.
  // Drag is local; the confirmation modal on exit decides whether to POST
  // the new order (so users see it) or revert it back to the snapshot.
  type MatReorderState = 'off' | 'picking' | 'editing';
  let matReorderState = $state<MatReorderState>('off');
  let matReorderCategoryId = $state<string | null>(null);
  let matReorderItems = $state<AdminMaterialDTO[] | null>(null);
  let matReorderOriginal = $state<AdminMaterialDTO[] | null>(null);
  let matReorderLoading = $state(false);
  let matReorderSaving = $state(false);
  let matReorderConfirmOpen = $state(false);

  const matReorderCategory = $derived(
    matReorderCategoryId && categories
      ? categories.find((c) => c.id === matReorderCategoryId) ?? null
      : null,
  );
  const matReorderDirty = $derived.by(() => {
    if (!matReorderItems || !matReorderOriginal) return false;
    if (matReorderItems.length !== matReorderOriginal.length) return true;
    return matReorderItems.some((m, i) => m.id !== matReorderOriginal![i].id);
  });

  /** Loads the category list on demand. Callers that need the list *before*
   *  rendering (the reorder picker) must await it; the tab effect fires it off. */
  async function loadCategories(): Promise<void> {
    try {
      const res = await api.adminCategories();
      categories = res.items;
    } catch {
      categories = [];
    }
  }

  async function enterMatReorderPicker(): Promise<void> {
    matReorderState = 'picking';
    if (categories === null) await loadCategories();
  }

  async function pickMatReorderCategory(catId: string): Promise<void> {
    matReorderCategoryId = catId;
    matReorderItems = null;
    matReorderOriginal = null;
    matReorderState = 'editing';
    matReorderLoading = true;
    try {
      const res = await api.adminMaterials({ categoryIds: [catId], limit: 500 });
      // Sort by sortOrder asc to match the user-facing section view — drag
      // results are predictable when admin sees the same order as users.
      const sorted = [...res.items].sort((a, b) => a.sortOrder - b.sortOrder);
      matReorderItems = sorted;
      matReorderOriginal = [...sorted];
    } catch {
      matReorderState = 'picking';
      matReorderCategoryId = null;
    } finally {
      matReorderLoading = false;
    }
  }

  function resetMatReorder(): void {
    matReorderState = 'off';
    matReorderCategoryId = null;
    matReorderItems = null;
    matReorderOriginal = null;
    matReorderConfirmOpen = false;
  }

  function requestExitMatReorder(): void {
    if (matReorderState === 'picking') {
      resetMatReorder();
      return;
    }
    if (matReorderState === 'editing') {
      if (matReorderDirty) {
        matReorderConfirmOpen = true;
      } else {
        resetMatReorder();
      }
    }
  }

  async function confirmSaveMatReorder(): Promise<void> {
    if (!matReorderItems || matReorderSaving) return;
    matReorderSaving = true;
    try {
      await api.adminReorderMaterials(matReorderItems.map((m) => m.id));
      resetMatReorder();
      await fetchMaterials();
    } catch {
      /* silent — admin still sees the editing view, can retry */
    } finally {
      matReorderSaving = false;
    }
  }

  function revertMatReorder(): void {
    resetMatReorder();
  }

  function onMatDndConsider(e: CustomEvent<DndEvent<AdminMaterialDTO>>): void {
    matReorderItems = e.detail.items;
  }
  function onMatDndFinalize(e: CustomEvent<DndEvent<AdminMaterialDTO>>): void {
    matReorderItems = e.detail.items;
  }

  const TYPE_LABELS: Record<MaterialType, string> = {
    video: 'Відео',
    audio: 'Аудіо',
    text: 'Текст',
  };

  async function fetchMaterials(opts: { append?: boolean } = {}): Promise<void> {
    if (opts.append) matLoadingMore = true;
    else matLoading = true;
    matError = null;
    try {
      const res = await api.adminMaterials({
        search: matSearchInput.trim() || undefined,
        type: matTypeFilter === 'all' ? undefined : matTypeFilter,
        categoryIds: matCategoryFilter.length > 0 ? matCategoryFilter : undefined,
        publish: matPublishFilter === 'all' ? undefined : matPublishFilter,
        page: opts.append ? matPage + 1 : 1,
        limit: 20,
      });
      if (opts.append) {
        materials = [...(materials ?? []), ...res.items];
        matPage = res.page;
      } else {
        materials = res.items;
        matPage = 1;
      }
      matTotal = res.total;
      matHasMore = res.hasMore;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        replace('/home');
        return;
      }
      matError = 'Не вдалось завантажити матеріали';
    } finally {
      matLoading = false;
      matLoadingMore = false;
    }
  }

  let matSearchTimer: ReturnType<typeof setTimeout> | undefined;
  function onMatSearchChange(e: Event): void {
    matSearchInput = (e.target as HTMLInputElement).value;
    if (matSearchTimer) clearTimeout(matSearchTimer);
    matSearchTimer = setTimeout(() => fetchMaterials(), 300);
  }
  function openFilterModal(): void {
    // Snapshot current state into drafts so the modal can be cancelled.
    draftType = matTypeFilter;
    draftCategoryIds = [...matCategoryFilter];
    draftPublish = matPublishFilter;
    catsExpanded = false;
    filterModalOpen = true;
  }
  function closeFilterModal(): void {
    filterModalOpen = false;
  }
  function applyFilters(): void {
    matTypeFilter = draftType;
    matCategoryFilter = draftCategoryIds;
    matPublishFilter = draftPublish;
    filterModalOpen = false;
    fetchMaterials();
  }
  function resetDraftFilters(): void {
    draftType = 'all';
    draftCategoryIds = [];
    draftPublish = 'all';
  }
  function toggleDraftCategory(id: string): void {
    draftCategoryIds = draftCategoryIds.includes(id)
      ? draftCategoryIds.filter((x) => x !== id)
      : [...draftCategoryIds, id];
  }
  /** Remove a single active filter chip from the main screen. */
  function removeTypeFilter(): void {
    matTypeFilter = 'all';
    fetchMaterials();
  }
  function removeCategoryFilter(id: string): void {
    matCategoryFilter = matCategoryFilter.filter((x) => x !== id);
    fetchMaterials();
  }
  function removePublishFilter(): void {
    matPublishFilter = 'all';
    fetchMaterials();
  }

  /** Optimistic toggle — flip locally, call API, revert on error. */
  async function togglePublished(m: AdminMaterialDTO): Promise<void> {
    if (!materials) return;
    const before = m.isPublished;
    togglingId = m.id;
    materials = materials.map((x) => (x.id === m.id ? { ...x, isPublished: !before } : x));
    try {
      const updated = await api.adminMaterialPatch(m.id, { isPublished: !before });
      materials = materials.map((x) => (x.id === updated.id ? updated : x));
    } catch {
      materials = materials.map((x) => (x.id === m.id ? { ...x, isPublished: before } : x));
    } finally {
      togglingId = null;
    }
  }

  // Throttle Bunny auto-sync — once per 30s across the whole session. Server
  // also has a mutex, this just avoids needless round-trips.
  const BUNNY_SYNC_THROTTLE_MS = 30_000;
  let lastBunnySyncAt = 0;
  function maybeBunnySync(): void {
    const now = Date.now();
    if (now - lastBunnySyncAt < BUNNY_SYNC_THROTTLE_MS) return;
    lastBunnySyncAt = now;
    api
      .adminBunnySync()
      .then((r) => {
        const anyMatChange =
          (r.updated ?? 0) > 0 || (r.materialsImported ?? 0) > 0;
        const anyCatChange =
          (r.collectionsImported ?? 0) > 0 ||
          (r.collectionsLinked ?? 0) > 0 ||
          (r.collectionsCreated ?? 0) > 0;
        if (anyMatChange) fetchMaterials();
        if (anyCatChange || (r.materialsImported ?? 0) > 0) {
          // materialsImported can change category totalMaterials too.
          api
            .adminCategories()
            .then((res) => (categories = res.items))
            .catch(() => {});
        }
      })
      .catch(() => {
        /* silent — admin can still browse stale-but-recent data */
      });
  }

  // Load Content tab data + categories on first entry.
  $effect(() => {
    if (activeTab === 'content') {
      if (materials === null && !matLoading) fetchMaterials();
      if (categories === null) void loadCategories();
      // Sync metadata from Bunny on Content tab entry — but throttled to once
      // per 30 s so flipping tabs doesn't fire a flurry of parallel syncs.
      maybeBunnySync();
    }
  });

  // Load Products tab data on first entry.
  $effect(() => {
    if (activeTab === 'products' && products === null) fetchProducts();
  });

  // Load Schedule tab data on first entry.
  $effect(() => {
    if (activeTab === 'schedule' && events === null) fetchEvents();
  });

  onMount(() => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) replace('/home');
    // Direct-URL or reload onto an admin page implies admin context; align
    // the mode so the user pages we navigate to from here show admin chrome.
    adminMode.set('admin');
    // Coming back from a contact/payment detail: reopen the drill-in we left.
    restoreDashboardModal();
  });
</script>

<div class="admin-screen">
  <div class="admin-header">
    <div class="admin-logo-row">
      <Logo size={40} />
      <div>
        <div class="admin-logo-text">{BRAND_NAME}</div>
        <div class="admin-role">Адміністратор</div>
      </div>
    </div>
    <div class="admin-header-actions">
      <a href="#/admin/settings" class="admin-exit">Налаштування</a>
      <a href="#/profile" class="admin-exit" onclick={() => adminMode.set('user')}>Вийти</a>
    </div>
  </div>

  <div class="admin-body">
    {#if activeTab === 'subscriptions'}
      <div class="sub-controls">
        <input
          class="sub-search"
          type="search"
          placeholder="Пошук: ім'я, username, email, телефон, TG ID…"
          value={searchInput}
          oninput={onSearchChange}
        />
        <button
          type="button"
          class="filter-btn icon-only"
          class:has-active={subsActiveFiltersCount > 0}
          onclick={openSubsFilterModal}
          aria-label="Відкрити фільтри"
          title="Фільтр"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6L14 14.4V20a1 1 0 0 1-1.5.87l-3-1.7A1 1 0 0 1 9 18.3V14.4L3.2 5.6A1 1 0 0 1 3 5z"/>
          </svg>
          {#if subsActiveFiltersCount > 0}
            <span class="filter-badge">{subsActiveFiltersCount}</span>
          {/if}
        </button>
      </div>

      {#if subsActiveFiltersCount > 0}
        <div class="filter-chips">
          {#each statusFilter as s (s)}
            <button
              type="button"
              class="filter-chip"
              onclick={() => removeStatusFilter(s)}
              aria-label="Прибрати фільтр {STATUS_LABEL[s]}"
            >
              {STATUS_LABEL[s]}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/each}
          {#each tariffFilter as t (t)}
            <button
              type="button"
              class="filter-chip"
              onclick={() => removeTariffFilter(t)}
              aria-label="Прибрати тариф {t}"
            >
              {t}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/each}
          {#if startFilter}
            <button
              type="button"
              class="filter-chip"
              onclick={removeStartFilter}
              aria-label="Прибрати фільтр початку"
            >
              Початок підписки: {dateFilterLabel(startFilter)}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/if}
          {#if endFilter}
            <button
              type="button"
              class="filter-chip"
              onclick={removeEndFilter}
              aria-label="Прибрати фільтр кінця"
            >
              Кінець підписки: {dateFilterLabel(endFilter)}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/if}
          {#if emailFilter}
            <button
              type="button"
              class="filter-chip"
              onclick={removeEmailFilter}
              aria-label="Прибрати фільтр email"
            >
              Email: {textFilterLabel(emailFilter)}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/if}
          {#if phoneFilter}
            <button
              type="button"
              class="filter-chip"
              onclick={removePhoneFilter}
              aria-label="Прибрати фільтр телефону"
            >
              Телефон: {textFilterLabel(phoneFilter)}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/if}
          {#if usernameFilter}
            <button
              type="button"
              class="filter-chip"
              onclick={removeUsernameFilter}
              aria-label="Прибрати фільтр username"
            >
              Username: {textFilterLabel(usernameFilter)}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          {/if}
        </div>
      {/if}

      {#if loading}
        <div class="sub-placeholder">Завантажуємо…</div>
      {:else if error}
        <div class="sub-placeholder error">{error}</div>
      {:else if users === null || users.length === 0}
        <div class="sub-placeholder">Нічого не знайдено</div>
      {:else}
        <div class="sub-total">Знайдено: {total}</div>
        <div class="sub-list">
          {#each users as u (u.telegramId)}
            <a class="sub-row" href={`#/admin/user/${u.telegramId}`}>
              <div class="sub-row-top">
                <div class="user-av">
                  {#if u.photoUrl}<img src={u.photoUrl} alt="" />{:else}{initials(u)}{/if}
                </div>
                <div class="user-text">
                  <div class="user-name">{u.fullName}</div>
                  <div class="user-meta">
                    {u.username ? '@' + u.username : 'ID ' + u.telegramId}{#if u.isAdmin} · <Icon name="shield-check" size="xs" /> admin{/if}
                  </div>
                </div>
                <span class="badge {STATUS_CLASS[u.status]}">{STATUS_LABEL[u.status]}</span>
              </div>
              <div class="sub-detail">
                <div class="sub-detail-item">
                  <span>{u.tariff ?? '—'}</span>
                  <div class="sub-detail-label">Тариф</div>
                </div>
                <div class="sub-detail-item">
                  <span>{formatDayMonth(u.subscriptionStartDate)}</span>
                  <div class="sub-detail-label">Початок</div>
                </div>
                <div class="sub-detail-item">
                  <span
                    class:date-red={u.status === 'inactive'}
                    class:date-yellow={u.status === 'active' && isExpiringSoon(u.subscriptionEndDate)}
                  >{formatDayMonth(u.subscriptionEndDate)}</span>
                  <div class="sub-detail-label">Кінець</div>
                </div>
              </div>
            </a>
          {/each}
        </div>
        {#if hasMore}
          <button
            type="button"
            class="sub-more"
            disabled={loadingMore}
            onclick={() => fetchUsers({ append: true })}
          >
            {loadingMore ? 'Завантажую…' : 'Завантажити ще'}
          </button>
        {/if}
      {/if}
    {:else if activeTab === 'content'}
      <!-- Sub-tab switcher: Розділи / Матеріали -->
      <div class="subtab-row">
        <button
          type="button"
          class="subtab"
          class:active={contentSubTab === 'categories'}
          onclick={() => (contentSubTab = 'categories')}
        >Розділи{#if categories} ({categories.length}){/if}</button>
        <button
          type="button"
          class="subtab"
          class:active={contentSubTab === 'materials'}
          onclick={() => (contentSubTab = 'materials')}
        >Матеріали{#if matTotal > 0 || materials !== null} ({matTotal}){/if}</button>
        <!-- Посилання, стилізоване під підтаб: сам екран живе окремим роутом
             (AdminCommentsPage), бо цей файл уже під 5000 рядків. -->
        <a class="subtab" href="#/admin/comments"
          >Коментарі{#if commentsPending > 0}<span class="subtab-badge">{commentsPending}</span>{/if}</a>
      </div>

      {#if !$session?.bunnyWritable}
        <div class="readonly-banner">
          <Icon name="eye" size="xs" /> Перегляд-режим: контент Bunny доступний лише для читання. Створення, залив і
          видалення вимкнені на цьому середовищі.
        </div>
      {/if}

      {#if contentSubTab === 'materials'}
        {#if matReorderState === 'off' && $session?.bunnyWritable}
          <a class="add-btn" href="#/admin/material/new">+ Створити матеріал</a>
        {/if}

        <div class="sub-controls">
          {#if matReorderState === 'picking'}
            <div class="reorder-hint">Оберіть розділ для зміни порядку матеріалів</div>
          {:else if matReorderState === 'editing'}
            <div class="reorder-hint">
              {matReorderCategory?.name ?? ''}
              — перетягуй <span class="reorder-hint-icon">⋮⋮</span> щоб змінити порядок
            </div>
          {:else}
            <input
              class="sub-search"
              type="search"
              placeholder="Пошук по назві або опису…"
              value={matSearchInput}
              oninput={onMatSearchChange}
            />
            <button
              type="button"
              class="filter-btn icon-only"
              class:has-active={activeFiltersCount > 0}
              onclick={openFilterModal}
              aria-label="Відкрити фільтри"
              title="Фільтр"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6L14 14.4V20a1 1 0 0 1-1.5.87l-3-1.7A1 1 0 0 1 9 18.3V14.4L3.2 5.6A1 1 0 0 1 3 5z"/>
              </svg>
              {#if activeFiltersCount > 0}
                <span class="filter-badge">{activeFiltersCount}</span>
              {/if}
            </button>
          {/if}
          <button
            type="button"
            class="filter-btn icon-only"
            class:has-active={matReorderState !== 'off'}
            onclick={matReorderState === 'off' ? enterMatReorderPicker : requestExitMatReorder}
            aria-label={matReorderState === 'off' ? 'Змінити порядок' : 'Завершити сортування'}
            title={matReorderState === 'off' ? 'Змінити порядок' : 'Завершити сортування'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M8 20V4"/>
              <path d="M4 8l4-4 4 4"/>
              <path d="M16 4v16"/>
              <path d="M12 16l4 4 4-4"/>
            </svg>
          </button>
        </div>

        {#if matReorderState === 'off' && activeFiltersCount > 0}
          <div class="filter-chips">
            {#if matTypeFilter !== 'all'}
              <button
                type="button"
                class="filter-chip"
                onclick={removeTypeFilter}
                aria-label="Прибрати фільтр типу"
              >
                <Icon name={MATERIAL_TYPE_ICONS[matTypeFilter]} size="xs" /> {TYPE_LABELS[matTypeFilter]}
                <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
              </button>
            {/if}
            {#each matCategoryFilter as id (id)}
              {@const cat = categories?.find((c) => c.id === id)}
              {#if cat}
                <button
                  type="button"
                  class="filter-chip"
                  onclick={() => removeCategoryFilter(id)}
                  aria-label="Прибрати фільтр {cat.name}"
                >
                  <Icon name={cat.icon} size="xs" /> {cat.name}
                  <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
                </button>
              {/if}
            {/each}
            {#if matPublishFilter !== 'all'}
              <button
                type="button"
                class="filter-chip"
                onclick={removePublishFilter}
                aria-label="Прибрати фільтр стану"
              >
                {PUBLISH_LABEL[matPublishFilter]}
                <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
              </button>
            {/if}
          </div>
        {/if}

        {#if matReorderState === 'picking'}
          {#if categories === null}
            <div class="sub-placeholder">Завантажуємо…</div>
          {:else if categories.length === 0}
            <div class="sub-placeholder">Спочатку створи хоча б один розділ</div>
          {:else}
            <div class="content-list">
              {#each categories as c (c.id)}
                <button
                  type="button"
                  class="content-row content-row-button"
                  onclick={() => pickMatReorderCategory(c.id)}
                >
                  <div class="content-thumb"><Icon name={c.icon} size="lg" /></div>
                  <div class="content-text">
                    <div class="content-name">{c.name}</div>
                    <div class="content-meta">
                      {c.totalMaterials} {c.totalMaterials === 1 ? 'матеріал' : c.totalMaterials < 5 ? 'матеріали' : 'матеріалів'}
                    </div>
                  </div>
                  <span class="content-chevron">›</span>
                </button>
              {/each}
            </div>
          {/if}
        {:else if matReorderState === 'editing'}
          {#if matReorderLoading}
            <div class="sub-placeholder">Завантажуємо…</div>
          {:else if !matReorderItems || matReorderItems.length === 0}
            <div class="sub-placeholder">У цьому розділі поки нема матеріалів</div>
          {:else}
            <section
              class="content-list"
              use:dndzone={{
                items: matReorderItems,
                dragDisabled: false,
                flipDurationMs: 200,
                type: 'admin-materials',
                dropTargetStyle: {},
              }}
              onconsider={onMatDndConsider}
              onfinalize={onMatDndFinalize}
            >
              {#each matReorderItems as m (m.id)}
                <div class="content-row reorder-row">
                  <div class="drag-handle" aria-hidden="true">
                    <svg width="10" height="16" viewBox="0 0 6 10" fill="currentColor">
                      <circle cx="1" cy="1" r="1"/><circle cx="5" cy="1" r="1"/>
                      <circle cx="1" cy="5" r="1"/><circle cx="5" cy="5" r="1"/>
                      <circle cx="1" cy="9" r="1"/><circle cx="5" cy="9" r="1"/>
                    </svg>
                  </div>
                  <div class="content-thumb"><Icon name={MATERIAL_TYPE_ICONS[m.type]} size="lg" /></div>
                  <div class="content-text">
                    <div class="content-name">
                      <span class="content-title">{m.title}</span>
                      {#if !m.isPublished}<span class="draft-badge">Чернетка</span>{/if}
                    </div>
                    <div class="content-meta">
                      {TYPE_LABELS[m.type]}{#if m.durationSec} · {durationLabel(m.durationSec)}{/if}
                    </div>
                  </div>
                </div>
              {/each}
            </section>
          {/if}
        {:else if matLoading}
          <div class="sub-placeholder">Завантажуємо…</div>
        {:else if matError}
          <div class="sub-placeholder error">{matError}</div>
        {:else if materials === null || materials.length === 0}
          <div class="sub-placeholder">Нічого не знайдено</div>
        {:else}
          <div class="content-list">
            {#each materials as m (m.id)}
              {#if m.category}
                <!-- Card body navigates to user-facing preview; pencil → admin edit -->
                <a class="content-row" href={`#/material/${m.id}`}>
                  <div class="content-thumb"><Icon name={MATERIAL_TYPE_ICONS[m.type]} size="lg" /></div>
                  <div class="content-text">
                    <div class="content-name">
                      <span class="content-title">{m.title}</span>
                      {#if m.bunnyMissingAt}
                        <span class="draft-badge draft-badge-error">Зникло з Bunny</span>
                      {:else if !m.isPublished}
                        <span class="draft-badge">Чернетка</span>
                      {/if}
                    </div>
                    <div class="content-meta">
                      <Icon name={m.category.icon} size="xs" /> {m.category.name} · {TYPE_LABELS[m.type]}{#if m.durationSec}
                        · {durationLabel(m.durationSec)}
                      {/if}
                    </div>
                    {#if m.tags.length > 0}
                      <div class="content-tags">
                        {#each m.tags as tag}<span class="content-tag">#{tag}</span>{/each}
                      </div>
                    {/if}
                  </div>
                  <button
                    type="button"
                    class="edit-btn"
                    aria-label="Скопіювати посилання"
                    title="Скопіювати посилання на матеріал"
                    onclick={(e) => copyDeepLink(e, m.id, materialDeepLink(m.id, $session?.botUsername))}
                  ><span class="link-icon">{#if copiedLinkId === m.id}<Icon name="check" />{:else}<Icon name="link" />{/if}</span></button>
                  <button
                    type="button"
                    class="edit-btn"
                    aria-label="Редагувати"
                    title="Редагувати"
                    onclick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      push(`/admin/material/${m.id}/edit`);
                    }}
                  ><span class="edit-icon"><Icon name="square-pen" /></span></button>
                </a>
              {:else}
                <!-- Uncategorized — user-facing 404s, so click goes to edit page -->
                <a class="content-row" href={`#/admin/material/${m.id}/edit`}>
                  <div class="content-thumb"><Icon name={MATERIAL_TYPE_ICONS[m.type]} size="lg" /></div>
                  <div class="content-text">
                    <div class="content-name">
                      <span class="content-title">{m.title}</span>
                      {#if m.bunnyMissingAt}
                        <span class="draft-badge draft-badge-error">Зникло з Bunny</span>
                      {:else}
                        <span class="draft-badge draft-badge-warn">Без розділу</span>
                      {/if}
                    </div>
                    <div class="content-meta">
                      {TYPE_LABELS[m.type]}{#if m.durationSec} · {durationLabel(m.durationSec)}{/if}
                    </div>
                  </div>
                  <button
                    type="button"
                    class="edit-btn"
                    aria-label="Скопіювати посилання"
                    title="Скопіювати посилання на матеріал"
                    onclick={(e) => copyDeepLink(e, m.id, materialDeepLink(m.id, $session?.botUsername))}
                  ><span class="link-icon">{#if copiedLinkId === m.id}<Icon name="check" />{:else}<Icon name="link" />{/if}</span></button>
                  <button
                    type="button"
                    class="edit-btn"
                    aria-label="Редагувати"
                    title="Редагувати"
                    onclick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      push(`/admin/material/${m.id}/edit`);
                    }}
                  ><span class="edit-icon"><Icon name="square-pen" /></span></button>
                </a>
              {/if}
            {/each}
          </div>
          {#if matHasMore}
            <button
              type="button"
              class="sub-more"
              disabled={matLoadingMore}
              onclick={() => fetchMaterials({ append: true })}
            >
              {matLoadingMore ? 'Завантажую…' : 'Завантажити ще'}
            </button>
          {/if}
        {/if}
      {:else}
        <!-- Розділи sub-tab — list of categories from /api/admin/categories -->
        {#if !reorderModeOn && $session?.bunnyWritable}
          <a class="add-btn" href="#/admin/category/new">+ Створити розділ</a>
        {/if}

        <div class="sub-controls">
          {#if reorderModeOn}
            <div class="reorder-hint">
              Перетягуй картки за <span class="reorder-hint-icon">⋮⋮</span> щоб змінити порядок
            </div>
          {:else}
            <input
              class="sub-search"
              type="search"
              placeholder="Пошук по назві розділу…"
              bind:value={catSearchInput}
            />
            <button
              type="button"
              class="filter-btn icon-only"
              class:has-active={catActiveFiltersCount > 0}
              onclick={openCatFilterModal}
              aria-label="Відкрити фільтри"
              title="Фільтр"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6L14 14.4V20a1 1 0 0 1-1.5.87l-3-1.7A1 1 0 0 1 9 18.3V14.4L3.2 5.6A1 1 0 0 1 3 5z"/>
              </svg>
              {#if catActiveFiltersCount > 0}
                <span class="filter-badge">{catActiveFiltersCount}</span>
              {/if}
            </button>
          {/if}
          <button
            type="button"
            class="filter-btn icon-only"
            class:has-active={reorderModeOn}
            onclick={reorderModeOn ? requestExitReorder : enterReorderMode}
            aria-label={reorderModeOn ? 'Завершити сортування' : 'Змінити порядок'}
            title={reorderModeOn ? 'Завершити сортування' : 'Змінити порядок'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <!-- Up arrow on the left -->
              <path d="M8 20V4"/>
              <path d="M4 8l4-4 4 4"/>
              <!-- Down arrow on the right -->
              <path d="M16 4v16"/>
              <path d="M12 16l4 4 4-4"/>
            </svg>
          </button>
        </div>

        {#if !reorderModeOn && catPublishFilter !== 'all'}
          <div class="filter-chips">
            <button
              type="button"
              class="filter-chip"
              onclick={removeCatPublishFilter}
              aria-label="Прибрати фільтр стану"
            >
              {PUBLISH_LABEL[catPublishFilter]}
              <span class="filter-chip-x" aria-hidden="true"><Icon name="x" size="xs" /></span>
            </button>
          </div>
        {/if}

        {#snippet categoryRow(c: AdminCategoryDTO)}
          <div class="content-thumb"><Icon name={c.icon} size="lg" /></div>
          <div class="content-text">
            <div class="content-name">
              {c.name}
              {#if !c.isActive}<span class="draft-badge">Чернетка</span>{/if}
            </div>
            <div class="content-meta">
              {c.totalMaterials} {c.totalMaterials === 1 ? 'матеріал' : c.totalMaterials < 5 ? 'матеріали' : 'матеріалів'}
            </div>
          </div>
        {/snippet}

        {#if categories === null}
          <div class="sub-placeholder">Завантажуємо…</div>
        {:else if !reorderModeOn && visibleCategories && visibleCategories.length === 0}
          <div class="sub-placeholder">Нічого не знайдено</div>
        {:else if reorderModeOn}
          <section
            class="content-list"
            use:dndzone={{
              items: categories,
              dragDisabled: false,
              flipDurationMs: 200,
              type: 'admin-categories',
              dropTargetStyle: {},
            }}
            onconsider={onDndConsider}
            onfinalize={onDndFinalize}
          >
            {#each categories as c (c.id)}
              <div class="content-row reorder-row">
                <div class="drag-handle" aria-hidden="true">
                  <svg width="10" height="16" viewBox="0 0 6 10" fill="currentColor">
                    <circle cx="1" cy="1" r="1"/><circle cx="5" cy="1" r="1"/>
                    <circle cx="1" cy="5" r="1"/><circle cx="5" cy="5" r="1"/>
                    <circle cx="1" cy="9" r="1"/><circle cx="5" cy="9" r="1"/>
                  </svg>
                </div>
                {@render categoryRow(c)}
              </div>
            {/each}
          </section>
        {:else}
          <div class="content-list">
            {#each visibleCategories ?? [] as c (c.id)}
              <a class="content-row" href={`#/section/${c.id}`}>
                {@render categoryRow(c)}
                <button
                  type="button"
                  class="edit-btn"
                  aria-label="Скопіювати посилання"
                  title="Скопіювати посилання на розділ"
                  onclick={(e) => copyDeepLink(e, c.id, sectionDeepLink(c.id, $session?.botUsername))}
                ><span class="link-icon">{#if copiedLinkId === c.id}<Icon name="check" />{:else}<Icon name="link" />{/if}</span></button>
                <button
                  type="button"
                  class="edit-btn"
                  aria-label="Редагувати"
                  title="Редагувати"
                  onclick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    push(`/admin/category/${c.id}/edit`);
                  }}
                ><span class="edit-icon"><Icon name="square-pen" /></span></button>
              </a>
            {/each}
          </div>
        {/if}
      {/if}
    {:else if activeTab === 'dashboard'}
      <div class="period-row">
        {#each PERIODS_PRIMARY as p (p)}
          <button
            type="button"
            class="chip-toggle"
            class:on={dashboardPeriod === p}
            onclick={() => setPeriod(p)}
          >{PERIOD_LABEL[p]}</button>
        {/each}
      </div>
      <div class="period-row">
        {#each PERIODS_SECONDARY as p (p)}
          <button
            type="button"
            class="chip-toggle"
            class:on={dashboardPeriod === p}
            onclick={() => setPeriod(p)}
          >{PERIOD_LABEL[p]}</button>
        {/each}
      </div>

      <select class="dash-product" bind:value={dashboardProduct} onchange={setProduct}>
        <option value="">Усі продукти</option>
        <option value="unmatched">Не зіставлені</option>
        {#each dashboardProducts as p (p.id)}
          <option value={p.id}>{p.title}</option>
        {/each}
      </select>

      {#if dashboardPeriod === 'dayPick'}
        <div class="month-nav">
          <button type="button" class="month-arrow" onclick={() => shiftDay(-1)} aria-label="Попередній день">‹</button>
          <div class="month-label">{formatFullDay(pickDay)}</div>
          <button type="button" class="month-arrow" onclick={() => shiftDay(1)} aria-label="Наступний день">›</button>
        </div>
      {/if}

      {#if dashboardPeriod === 'monthPick'}
        <div class="month-nav">
          <button type="button" class="month-arrow" onclick={() => shiftMonth(-1)} aria-label="Попередній місяць">‹</button>
          <div class="month-label">{MONTH_NAMES[pickMonth]} {pickYear}</div>
          <button type="button" class="month-arrow" onclick={() => shiftMonth(1)} aria-label="Наступний місяць">›</button>
        </div>
      {/if}

      {#if dashboardPeriod === 'yearPick'}
        <div class="month-nav">
          <button type="button" class="month-arrow" onclick={() => shiftYear(-1)} aria-label="Попередній рік">‹</button>
          <div class="month-label">{pickYear}</div>
          <button type="button" class="month-arrow" onclick={() => shiftYear(1)} aria-label="Наступний рік">›</button>
        </div>
      {/if}

      {#if dashboardPeriod === 'custom'}
        <div class="range-row">
          <DatePicker
            mode="range"
            from={customFrom}
            to={customTo}
            placeholder="Оберіть період"
            onRange={(f, t) => {
              customFrom = f;
              customTo = t;
            }}
            onApply={applyCustomRange}
          />
        </div>
      {/if}

      {#if dashboardPeriod === 'custom' && !customReady && !dashboardStats}
        <div class="sub-placeholder">Оберіть період «від» та/або «до» і натисніть «Застосувати».</div>
      {:else if dashboardLoading && !dashboardStats}
        <div class="sub-placeholder">Завантажуємо…</div>
      {:else if dashboardError && !dashboardStats}
        <div class="sub-placeholder error">{dashboardError}</div>
      {:else if dashboardStats}
        {@const s = dashboardStats}
        {@const maxActivity = Math.max(1, ...s.activityByDay.map((d) => d.count))}
        <!-- Дельти плиток оплат: {@const} має бути прямим нащадком блоку, тож
             рахуємо їх тут, а не всередині карток. -->
        {@const dGross = delta(s.payments.grossCents, s.prev?.grossCents, false, uah)}
        {@const dCount = delta(s.payments.count, s.prev?.count)}
        {@const dAvg = delta(s.payments.avgCents, s.prev?.avgCents, false, uah)}
        {@const dRef = delta(s.payments.refundCount, s.prev?.refundCount, true)}
        {@const periodWord = dashboardPeriod === 'all' ? 'за весь час' : dashboardPeriod === 'custom' ? 'за період' : dashboardPeriod === 'monthPick' ? `за ${MONTH_NAMES[pickMonth].toLowerCase()} ${pickYear}` : dashboardPeriod === 'dayPick' ? `за ${formatFullDay(pickDay)}` : dashboardPeriod === 'yearPick' ? `за ${pickYear}` : `за ${PERIOD_LABEL[dashboardPeriod].toLowerCase()}`}
        <div class="stat-grid">
          {#snippet metricCard(metric: DashboardMetric, label: string, value: number | string, numClass: string, trend: string, trendClass: string, d: ReturnType<typeof delta> = null)}
            <div
              class="stat-card clickable"
              role="button"
              tabindex="0"
              onclick={() => openMembers(metric, label)}
              onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openMembers(metric, label)}
            >
              <div class="stat-label">{label}</div>
              <div class="stat-num-row">
                <div class="stat-num {numClass}">{value}</div>
                {#if d}<span class="stat-delta {d.cls}" title={d.title}>{d.text}</span>{/if}
              </div>
              <div class="stat-trend {trendClass}">{trend} ›</div>
            </div>
          {/snippet}
          {@render metricCard('active', 'Активні учасниці', s.activeMembers, 'pu', 'із доступом', '', delta(s.activeMembers, s.prev?.activeMembers))}
          {@render metricCard('new', `Нові ${periodWord}`, s.payments.newCount, 'green', 'отримали доступ вперше', '', delta(s.payments.newCount, s.prev?.newCount))}
          {@render metricCard(
            'renewed',
            `Продовження ${periodWord}`,
            s.renewals.applicable ? s.renewals.renewedCount : '—',
            '',
            s.renewals.applicable
              ? `з ${s.renewals.dueCount} · ${s.renewals.ratePct}% · ${uah(s.renewals.renewedRevenueCents)}`
              : 'продукт без підписки',
            '',
            s.renewals.applicable ? delta(s.renewals.renewedCount, s.prev?.renewedCount) : null,
          )}
          {@render metricCard(
            'ended',
            'Підписка закінчилася',
            s.renewals.applicable ? s.renewals.lapsedCount : '—',
            'red',
            s.renewals.pendingCount > 0
              ? `не поновили · ${s.renewals.pendingCount} ще в межах 7 днів`
              : `не поновили ${periodWord}`,
            'trend-red',
            s.renewals.applicable ? delta(s.renewals.lapsedCount, s.prev?.lapsedCount, true) : null,
          )}
        </div>

        <div class="sec-row">
          <div class="sec-title">Оплати {periodWord}</div>
          <a class="sec-link" href="#/admin/payments">Усі оплати →</a>
        </div>
        <div class="stat-grid">
          <div
            class="stat-card clickable"
            role="button"
            tabindex="0"
            onclick={() => openRevenueDetail('gross')}
            onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openRevenueDetail('gross')}
          >
            <div class="stat-label">Виручка брутто</div>
            <div class="stat-num-row">
              <div class="stat-num pu">{uah(s.payments.grossCents)}</div>
              {#if dGross}<span class="stat-delta {dGross.cls}" title={dGross.title}>{dGross.text}</span>{/if}
            </div>
            <div class="stat-trend">
              {#if s.payments.refundCents > 0}
                нетто {uah(s.payments.netCents)} ({uah(s.payments.refundCents)} повернень) ›
              {:else}
                {s.payments.count} оплат ›
              {/if}
            </div>
          </div>
          <div
            class="stat-card clickable"
            role="button"
            tabindex="0"
            onclick={() => openPayments('succeeded', `Оплати ${periodWord}`)}
            onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openPayments('succeeded', `Оплати ${periodWord}`)}
          >
            <div class="stat-label">Оплат</div>
            <div class="stat-num-row">
              <div class="stat-num">{s.payments.count}</div>
              {#if dCount}<span class="stat-delta {dCount.cls}" title={dCount.title}>{dCount.text}</span>{/if}
            </div>
            <div class="stat-trend">успішних ›</div>
          </div>
          <div
            class="stat-card clickable"
            role="button"
            tabindex="0"
            onclick={() => openRevenueDetail('avg')}
            onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openRevenueDetail('avg')}
          >
            <div class="stat-label">Середній чек</div>
            <div class="stat-num-row">
              <div class="stat-num green">{uah(s.payments.avgCents)}</div>
              {#if dAvg}<span class="stat-delta {dAvg.cls}" title={dAvg.title}>{dAvg.text}</span>{/if}
            </div>
            <div class="stat-trend">на оплату ›</div>
          </div>
          <div
            class="stat-card clickable"
            role="button"
            tabindex="0"
            onclick={() => openPayments('refunded', `Повернення ${periodWord}`)}
            onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openPayments('refunded', `Повернення ${periodWord}`)}
          >
            <div class="stat-label">Повернення</div>
            <div class="stat-num-row">
              <div class="stat-num orange">{s.payments.refundCount}</div>
              {#if dRef}<span class="stat-delta {dRef.cls}" title={dRef.title}>{dRef.text}</span>{/if}
            </div>
            <div class="stat-trend trend-orange">{uah(s.payments.refundCents)} ›</div>
          </div>
        </div>

        {#if s.revenueByProduct.length > 0}
          <div class="sec-title">Виручка по продуктах</div>
          {#each s.revenueByProduct as r (r.productId ?? 'none')}
            {#if r.productId}
              <div class="product-block">
                <div
                  class="product-head clickable"
                  role="button"
                  tabindex="0"
                  onclick={() => openTariffs(r.productId!, r.title)}
                  onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openTariffs(r.productId!, r.title)}
                >
                  <div class="content-thumb"><Icon name="credit-card" size="lg" /></div>
                  <div style="flex:1; min-width: 0;">
                    <div class="content-name">{r.title} ›</div>
                    <div class="content-meta-text">{r.count} оплат</div>
                  </div>
                  <div class="row-amount">{uah(r.netCents)}</div>
                </div>
                {#if r.tariffs.length > 0}
                  <div class="tariff-inner">
                    {#each r.tariffs as t (t.tariffId ?? 'none')}
                      <div class="tariff-inner-row">
                        <span class="tariff-inner-name">{t.title}</span>
                        <span class="tariff-inner-count">{t.count} опл</span>
                        <span class="tariff-inner-amount">{uah(t.netCents)}</span>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {:else}
              <div class="content-row">
                <div class="content-thumb"><Icon name="circle-help" size="lg" /></div>
                <div style="flex:1; min-width: 0;">
                  <div class="content-name">{r.title}</div>
                  <div class="content-meta-text">{r.count} оплат</div>
                </div>
                <div class="row-amount">{uah(r.netCents)}</div>
              </div>
            {/if}
          {/each}
        {/if}

        <!-- UTM: три незалежні числа на рядок — нові контакти, гроші за міткою
             самої оплати і гроші за first-touch платника. Останнє відповідає на
             «який креатив приносить гроші» і з другим навмисно не збігається. -->
        <div class="sec-title utm-sec-title">UTM-мітки</div>
        <div class="utm-tabs">
          {#each UTM_DIM_TABS as t (t.key)}
            <button
              type="button"
              class="utm-tab"
              class:active={utmDim === t.key}
              onclick={() => setUtmDim(t.key)}
            >{t.label}</button>
          {/each}
        </div>
        {#if utmLoading && !utmReport}
          <div class="sub-placeholder">Завантажуємо…</div>
        {:else if utmError}
          <div class="sub-placeholder">{utmError}</div>
        {:else if !utmReport || utmReport.items.length === 0}
          <div class="sub-placeholder">
            Даних ще немає — мітки збираються з моменту впровадження.
          </div>
        {:else}
          <div class="utm-table">
            <div class="utm-row utm-row-head">
              <span class="utm-c-name">Мітка</span>
              <span class="utm-c-num" title="Нові контакти: зʼявились у цьому періоді">Контакти</span>
              <span class="utm-c-num" title="Виручка оплат, на яких стоїть ця мітка">За оплатою</span>
              <span class="utm-c-num" title="Виручка оплат, згрупована по first-touch мітці платника">За first-touch</span>
            </div>
            {#each utmReport.items as r (r.value ?? 'none')}
              <div
                class="utm-row clickable"
                role="button"
                tabindex="0"
                onclick={() => openUtmPayments(r)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openUtmPayments(r)}
              >
                <span class="utm-c-name">{r.title}</span>
                <span class="utm-c-num">{r.contacts}</span>
                <span class="utm-c-num">{uah(r.payNetCents)}<small>{r.payCount}</small></span>
                <span class="utm-c-num strong">{uah(r.ftNetCents)}<small>{r.ftCount}</small></span>
              </div>
            {/each}
            <div class="utm-row utm-row-total">
              <span class="utm-c-name">Разом</span>
              <span class="utm-c-num">{utmReport.totals.contacts}</span>
              <span class="utm-c-num">{uah(utmReport.totals.payNetCents)}</span>
              <span class="utm-c-num strong">{uah(utmReport.totals.ftNetCents)}</span>
            </div>
          </div>
          {#if dashboardProduct}
            <div class="utm-note">
              Фільтр продукту звужує лише оплати — контакт не належить продукту.
            </div>
          {/if}
        {/if}

        <div class="sec-row activity-row">
          <div class="sec-title">Активність по днях</div>
          <a class="sec-link" href="#/admin/activity">Детальніше →</a>
        </div>
        <div class="chart-card">
          <div class="chart-bar-row">
            {#each s.activityByDay as d (d.date)}
              <div
                class="chart-bar"
                class:active-bar={d.count > 0}
                class:clickable={d.count > 0}
                role="button"
                tabindex="0"
                style="height: {Math.max(4, Math.round((d.count / maxActivity) * 100))}%"
                title={`${d.weekday}: ${d.count}`}
                onclick={() => d.count > 0 && openActivity(d.date, d.weekday)}
                onkeydown={(e) => d.count > 0 && (e.key === 'Enter' || e.key === ' ') && openActivity(d.date, d.weekday)}
              ></div>
            {/each}
          </div>
          <div class="chart-labels">
            {#each s.activityByDay as d (d.date)}
              <button
                type="button"
                class="chart-label"
                class:clickable={d.count > 0}
                onclick={() => d.count > 0 && openActivity(d.date, d.weekday)}
              >{dayMonth(d.date)}</button>
            {/each}
          </div>
        </div>

        <div class="sec-row">
          <div class="sec-title">Популярні матеріали</div>
          {#if s.popularMaterials.length > 0}
            <button type="button" class="sec-link as-button" onclick={openPopular}>Весь список →</button>
          {/if}
        </div>
        {#if s.popularMaterials.length === 0}
          <div class="sub-placeholder">Поки ніхто не додав у вподобані</div>
        {:else}
          {#each s.popularMaterials as m (m.id)}
            <a class="content-row" href={m.category ? `#/material/${m.id}` : `#/admin/material/${m.id}/edit`}>
              <div class="content-thumb"><Icon name={MATERIAL_TYPE_ICONS[m.type]} size="lg" /></div>
              <div style="flex:1; min-width: 0;">
                <div class="content-name">{m.title}</div>
                <div class="content-meta-text">
                  <Icon name="heart" size="xs" filled /> {m.likesCount} {m.likesCount === 1 ? 'людина' : 'людей'}{#if m.category} · <Icon name={m.category.icon} size="xs" /> {m.category.name}{/if}
                </div>
              </div>
            </a>
          {/each}
        {/if}
      {/if}
    {:else if activeTab === 'products'}
      <a class="add-btn" href="#/admin/product/new">+ Створити продукт</a>

      <div class="sub-controls">
        {#if productsReorderOn}
          <div class="reorder-hint">
            Перетягуй картки за <span class="reorder-hint-icon">⋮⋮</span> щоб змінити порядок
          </div>
        {:else}
          <input
            class="sub-search"
            type="search"
            placeholder="Пошук по назві продукту…"
            bind:value={productsSearch}
          />
        {/if}
        <button
          type="button"
          class="filter-btn icon-only"
          class:has-active={productsReorderOn}
          onclick={() => (productsReorderOn = !productsReorderOn)}
          aria-label={productsReorderOn ? 'Завершити сортування' : 'Змінити порядок'}
          title={productsReorderOn ? 'Завершити сортування' : 'Змінити порядок'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M8 20V4"/><path d="M4 8l4-4 4 4"/>
            <path d="M16 4v16"/><path d="M12 16l4 4 4-4"/>
          </svg>
        </button>
      </div>

      {#if !productsReorderOn}
        <div class="prod-filter-row">
          <button
            type="button"
            class="chip-toggle"
            class:on={productsScopeFilter === 'all'}
            onclick={() => (productsScopeFilter = productsScopeFilter === 'all' ? null : 'all')}
          >Простір</button>
          <button
            type="button"
            class="chip-toggle"
            class:on={productsScopeFilter === 'selected'}
            onclick={() => (productsScopeFilter = productsScopeFilter === 'selected' ? null : 'selected')}
          >Міні-курси</button>
        </div>
      {/if}

      {#snippet productRow(p: AdminProductDTO)}
        <div class="content-thumb">{#if p.accessScope === 'all'}<Icon name="crown" size="lg" />{:else}<Icon name="gift" size="lg" />{/if}</div>
        <div class="content-text">
          <div class="content-name">
            {p.title}
            {#if p.isSystem}<span class="draft-badge sys">Системний</span>{/if}
          </div>
          <div class="content-meta">
            {scopeLabel(p.accessScope)}{p.accessScope === 'selected' ? ` · ${p.categoriesCount} ${p.categoriesCount === 1 ? 'група' : p.categoriesCount < 5 ? 'групи' : 'груп'}` : ''}
          </div>
        </div>
      {/snippet}

      {#if products === null}
        <div class="sub-placeholder">Завантажуємо…</div>
      {:else if !productsReorderOn && visibleProducts && visibleProducts.length === 0}
        <div class="sub-placeholder">Нічого не знайдено</div>
      {:else if productsReorderOn}
        <section
          class="content-list"
          use:dndzone={{
            items: products,
            dragDisabled: false,
            flipDurationMs: 200,
            type: 'admin-products',
            dropTargetStyle: {},
          }}
          onconsider={onProductsDndConsider}
          onfinalize={onProductsDndFinalize}
        >
          {#each products as p (p.id)}
            <div class="content-row reorder-row">
              <div class="drag-handle" aria-hidden="true">
                <svg width="10" height="16" viewBox="0 0 6 10" fill="currentColor">
                  <circle cx="1" cy="1" r="1"/><circle cx="5" cy="1" r="1"/>
                  <circle cx="1" cy="5" r="1"/><circle cx="5" cy="5" r="1"/>
                  <circle cx="1" cy="9" r="1"/><circle cx="5" cy="9" r="1"/>
                </svg>
              </div>
              {@render productRow(p)}
            </div>
          {/each}
        </section>
      {:else}
        <div class="content-list">
          {#each visibleProducts ?? [] as p (p.id)}
            <a class="content-row" href={`#/admin/product/${p.id}/edit`}>
              {@render productRow(p)}
              <span class="edit-icon"><Icon name="square-pen" /></span>
            </a>
          {/each}
        </div>
      {/if}
    {:else if activeTab === 'schedule'}
      <a class="add-btn" href="#/admin/event/new">+ Створити подію</a>
      <div class="period-row">
        <button type="button" class="chip-toggle" class:on={scheduleView === 'list'} onclick={() => (scheduleView = 'list')}>Список</button>
        <button type="button" class="chip-toggle" class:on={scheduleView === 'calendar'} onclick={() => (scheduleView = 'calendar')}>Календар</button>
      </div>
      {#if events === null}
        <div class="sub-placeholder">Завантажуємо…</div>
      {:else if scheduleView === 'list'}
        {#if events.length === 0}
          <div class="sub-placeholder">Подій ще немає</div>
        {:else}
          {#each events as ev (ev.id)}
            <a class="content-row" href={`#/admin/event/${ev.id}/edit`}>
              <div class="content-thumb"><Icon name="calendar-days" size="lg" /></div>
              <div style="flex:1; min-width:0;">
                <div class="content-name">{ev.title}</div>
                <div class="content-meta-text">
                  {eventWhen(ev.startsAt)}{#if ev.hasRecording} · <Icon name="clapperboard" size="xs" /> запис{/if}
                </div>
              </div>
              <span class="row-chevron" aria-hidden="true">›</span>
            </a>
          {/each}
        {/if}
      {:else}
        <div class="month-nav">
          <button type="button" class="month-arrow" onclick={() => shiftCalMonth(-1)} aria-label="Попередній місяць">‹</button>
          <div class="month-label">{calMonthLabel}</div>
          <button type="button" class="month-arrow" onclick={() => shiftCalMonth(1)} aria-label="Наступний місяць">›</button>
        </div>
        <div class="sched-cal">
          <div class="sched-cal-head">
            {#each SCHED_WD as w (w)}<div class="sched-hcell">{w}</div>{/each}
          </div>
          <div class="sched-cal-grid">
            {#each calGrid as c, i (i)}
              {#if c === null}
                <div class="sched-cell empty"></div>
              {:else}
                <button
                  type="button"
                  class="sched-cell"
                  class:has={c.count > 0}
                  class:sel={calSelectedDay === c.date}
                  onclick={() => c.count > 0 && (calSelectedDay = calSelectedDay === c.date ? null : c.date)}
                >
                  <span class="sched-day">{c.day}</span>
                  {#if c.count > 0}<span class="sched-count">{c.count}</span>{/if}
                </button>
              {/if}
            {/each}
          </div>
        </div>
        {#if calSelectedDay && selectedDayEvents.length > 0}
          {#each selectedDayEvents as ev (ev.id)}
            <a class="content-row" href={`#/admin/event/${ev.id}/edit`}>
              <div class="content-thumb"><Icon name="calendar" size="lg" /></div>
              <div style="flex:1; min-width:0;">
                <div class="content-name">{ev.title}</div>
                <div class="content-meta-text">{hhmm(ev.startsAt)}{#if ev.hasRecording} · <Icon name="clapperboard" size="xs" /> запис{/if}</div>
              </div>
              <span class="row-chevron" aria-hidden="true">›</span>
            </a>
          {/each}
        {:else if scheduleView === 'calendar' && events.length > 0}
          <div class="cal-hint">Натисни день із подіями, щоб побачити список.</div>
        {/if}
      {/if}
    {/if}
  </div>

  <nav class="admin-nav">
    <div class="admin-nav-indicator" style="--idx: {activeIndex}"></div>
    <button
      type="button"
      class="admin-nav-item"
      class:active={activeTab === 'dashboard'}
      onclick={() => (activeTab = 'dashboard')}
    >
      <div class="admin-nav-icon"><Icon name="chart-column" size="nav" /></div>
      <div class="admin-nav-label">Дашборд</div>
    </button>
    <button
      type="button"
      class="admin-nav-item"
      class:active={activeTab === 'subscriptions'}
      onclick={() => (activeTab = 'subscriptions')}
    >
      <div class="admin-nav-icon"><Icon name="users-round" size="nav" /></div>
      <div class="admin-nav-label">Підписки</div>
    </button>
    <button
      type="button"
      class="admin-nav-item"
      class:active={activeTab === 'content'}
      onclick={() => (activeTab = 'content')}
    >
      <div class="admin-nav-icon"><Icon name="clapperboard" size="nav" /></div>
      <div class="admin-nav-label">Контент</div>
    </button>
    <button
      type="button"
      class="admin-nav-item"
      class:active={activeTab === 'products'}
      onclick={() => (activeTab = 'products')}
    >
      <div class="admin-nav-icon"><Icon name="gift" size="nav" /></div>
      <div class="admin-nav-label">Продукти</div>
    </button>
    <button
      type="button"
      class="admin-nav-item"
      class:active={activeTab === 'schedule'}
      onclick={() => (activeTab = 'schedule')}
    >
      <div class="admin-nav-icon"><Icon name="calendar-days" size="nav" /></div>
      <div class="admin-nav-label">Розклад</div>
    </button>
  </nav>

  {#if subsFilterModalOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeSubsFilterModal}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Фільтри">
      <div class="modal-head">
        <div class="modal-title">Фільтри</div>
        <button
          type="button"
          class="modal-close"
          onclick={closeSubsFilterModal}
          aria-label="Закрити"
        ><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <div class="modal-section-title">Статус підписки</div>
        <div class="type-pills">
          <button
            type="button"
            class="type-pill"
            class:active={draftStatuses.includes('active')}
            onclick={() => toggleDraftStatus('active')}
          >Активна</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftStatuses.includes('inactive')}
            onclick={() => toggleDraftStatus('inactive')}
          >Закінчилась</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftStatuses.includes('none')}
            onclick={() => toggleDraftStatus('none')}
          >Без підписки</button>
        </div>

        <div class="modal-section-title">Тариф</div>
        {#if availableTariffs === null}
          <div class="sub-placeholder">Завантажуємо…</div>
        {:else if availableTariffs.length === 0}
          <div class="sub-placeholder">Ще немає тарифів у БД</div>
        {:else}
          <div class="type-pills">
            {#each availableTariffs as t (t)}
              <button
                type="button"
                class="type-pill"
                class:active={draftTariffs.includes(t)}
                onclick={() => toggleDraftTariff(t)}
              >{t}</button>
            {/each}
          </div>
        {/if}

        <div class="modal-section-title">Початок підписки</div>
        <div class="date-filter">
          <select
            class="date-op"
            value={draftStartOp}
            onchange={(e) => (draftStartOp = (e.target as HTMLSelectElement).value as '' | DateOp)}
          >
            <option value="">— будь-яка —</option>
            <option value="before">до</option>
            <option value="after">після</option>
            <option value="on">дорівнює</option>
            <option value="between">між</option>
          </select>
          {#if draftStartOp === 'between'}
            <DatePicker
              mode="range"
              rangeTime
              from={draftStartDate1}
              to={draftStartDate2}
              placeholder="Оберіть період"
              applyLabel="Обрати"
              onRange={(f, t) => {
                draftStartDate1 = f;
                draftStartDate2 = t;
              }}
            />
          {:else if draftStartOp === 'on'}
            <DatePicker value={draftStartDate1} onChange={(v) => (draftStartDate1 = v)} />
          {:else if draftStartOp}
            <DatePicker withTime value={draftStartDate1} onChange={(v) => (draftStartDate1 = v)} />
          {/if}
        </div>

        <div class="modal-section-title">Кінець підписки</div>
        <div class="date-filter">
          <select
            class="date-op"
            value={draftEndOp}
            onchange={(e) => (draftEndOp = (e.target as HTMLSelectElement).value as '' | DateOp)}
          >
            <option value="">— будь-яка —</option>
            <option value="before">до</option>
            <option value="after">після</option>
            <option value="on">дорівнює</option>
            <option value="between">між</option>
          </select>
          {#if draftEndOp === 'between'}
            <DatePicker
              mode="range"
              rangeTime
              from={draftEndDate1}
              to={draftEndDate2}
              placeholder="Оберіть період"
              applyLabel="Обрати"
              onRange={(f, t) => {
                draftEndDate1 = f;
                draftEndDate2 = t;
              }}
            />
          {:else if draftEndOp === 'on'}
            <DatePicker value={draftEndDate1} onChange={(v) => (draftEndDate1 = v)} />
          {:else if draftEndOp}
            <DatePicker withTime value={draftEndDate1} onChange={(v) => (draftEndDate1 = v)} />
          {/if}
        </div>

        <div class="modal-section-title">Email</div>
        <div class="date-filter">
          <select
            class="date-op"
            value={draftEmailOp}
            onchange={(e) => (draftEmailOp = (e.target as HTMLSelectElement).value as '' | TextOp)}
          >
            <option value="">— будь-який —</option>
            <option value="empty">пуста</option>
            <option value="notEmpty">не пуста</option>
            <option value="contains">містить</option>
            <option value="notContains">не містить</option>
            <option value="equals">дорівнює</option>
            <option value="notEquals">не дорівнює</option>
          </select>
          {#if draftEmailOp && textOpNeedsValue(draftEmailOp as TextOp)}
            <input
              type="text"
              class="date-input"
              placeholder="значення…"
              value={draftEmailValue}
              oninput={(e) => (draftEmailValue = (e.target as HTMLInputElement).value)}
            />
          {/if}
        </div>

        <div class="modal-section-title">Телефон</div>
        <div class="date-filter">
          <select
            class="date-op"
            value={draftPhoneOp}
            onchange={(e) => (draftPhoneOp = (e.target as HTMLSelectElement).value as '' | TextOp)}
          >
            <option value="">— будь-який —</option>
            <option value="empty">пуста</option>
            <option value="notEmpty">не пуста</option>
            <option value="contains">містить</option>
            <option value="notContains">не містить</option>
            <option value="equals">дорівнює</option>
            <option value="notEquals">не дорівнює</option>
          </select>
          {#if draftPhoneOp && textOpNeedsValue(draftPhoneOp as TextOp)}
            <input
              type="tel"
              class="date-input"
              placeholder="значення…"
              value={draftPhoneValue}
              oninput={(e) => (draftPhoneValue = (e.target as HTMLInputElement).value)}
            />
          {/if}
        </div>

        <div class="modal-section-title">Username</div>
        <div class="date-filter">
          <select
            class="date-op"
            value={draftUsernameOp}
            onchange={(e) => (draftUsernameOp = (e.target as HTMLSelectElement).value as '' | TextOp)}
          >
            <option value="">— будь-який —</option>
            <option value="empty">пуста</option>
            <option value="notEmpty">не пуста</option>
            <option value="contains">містить</option>
            <option value="notContains">не містить</option>
            <option value="equals">дорівнює</option>
            <option value="notEquals">не дорівнює</option>
          </select>
          {#if draftUsernameOp && textOpNeedsValue(draftUsernameOp as TextOp)}
            <input
              type="text"
              class="date-input"
              placeholder="значення…"
              value={draftUsernameValue}
              oninput={(e) => (draftUsernameValue = (e.target as HTMLInputElement).value)}
            />
          {/if}
        </div>

        <div class="modal-section-title">Звідки прийшов (UTM)</div>
        <UtmFilter scope="contacts" bind:selected={draftUtm} />
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          onclick={resetSubsDraftFilters}
          disabled={draftStatuses.length === 0 &&
            draftTariffs.length === 0 &&
            !draftStartOp &&
            !draftEndOp &&
            !draftEmailOp &&
            !draftPhoneOp &&
            !draftUsernameOp &&
            utmCount(draftUtm) === 0}
        >Скинути</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          onclick={applySubsFilters}
        >Застосувати</button>
      </div>
    </div>
  {/if}

  {#if catFilterModalOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeCatFilterModal}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Фільтри розділів">
      <div class="modal-head">
        <div class="modal-title">Фільтри</div>
        <button
          type="button"
          class="modal-close"
          onclick={closeCatFilterModal}
          aria-label="Закрити"
        ><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <div class="modal-section-title">Стан публікації</div>
        <div class="type-pills">
          <button
            type="button"
            class="type-pill"
            class:active={draftCatPublish === 'all'}
            onclick={() => (draftCatPublish = 'all')}
          >Всі</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftCatPublish === 'published'}
            onclick={() => (draftCatPublish = 'published')}
          >Опубліковано</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftCatPublish === 'draft'}
            onclick={() => (draftCatPublish = 'draft')}
          >Чернетка</button>
        </div>
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          onclick={resetCatDraftFilters}
          disabled={draftCatPublish === 'all'}
        >Скинути</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          onclick={applyCatFilters}
        >Застосувати</button>
      </div>
    </div>
  {/if}

  {#if filterModalOpen}
    <!-- Click on the dimmed backdrop closes the modal — same behaviour as the
         the close button. Drafts are discarded; main filters stay untouched. -->
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeFilterModal}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Фільтри">
      <div class="modal-head">
        <div class="modal-title">Фільтри</div>
        <button
          type="button"
          class="modal-close"
          onclick={closeFilterModal}
          aria-label="Закрити"
        ><Icon name="x" /></button>
      </div>

      <div class="modal-body">
        <div class="modal-section-title">Тип матеріалу</div>
        <div class="type-pills">
          <button
            type="button"
            class="type-pill"
            class:active={draftType === 'all'}
            onclick={() => (draftType = 'all')}
          >Всі</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftType === 'video'}
            onclick={() => (draftType = 'video')}
          >▶️ Відео</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftType === 'audio'}
            onclick={() => (draftType = 'audio')}
          ><Icon name="audio-lines" size="xs" /> Аудіо</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftType === 'text'}
            onclick={() => (draftType = 'text')}
          ><Icon name="file-text" size="xs" /> Текст</button>
        </div>

        <div class="modal-section-title">Розділи</div>
        <div class="cat-checks" class:collapsed={!catsExpanded}>
          {#if categories}
            {#each categories as c (c.id)}
              <label class="cat-check" class:active={draftCategoryIds.includes(c.id)}>
                <input
                  type="checkbox"
                  checked={draftCategoryIds.includes(c.id)}
                  onchange={() => toggleDraftCategory(c.id)}
                />
                <span class="cat-check-label"><Icon name={c.icon} size="xs" /> {c.name}</span>
                <span class="cat-check-count">{c.totalMaterials}</span>
              </label>
            {/each}
          {/if}
        </div>
        {#if categories && categories.length > 6}
          <button
            type="button"
            class="cat-toggle"
            onclick={() => (catsExpanded = !catsExpanded)}
          >
            {catsExpanded ? 'Згорнути' : `Показати всі (${categories.length})`}
          </button>
        {/if}

        <div class="modal-section-title">Стан публікації</div>
        <div class="type-pills">
          <button
            type="button"
            class="type-pill"
            class:active={draftPublish === 'all'}
            onclick={() => (draftPublish = 'all')}
          >Всі</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftPublish === 'published'}
            onclick={() => (draftPublish = 'published')}
          >Опубліковано</button>
          <button
            type="button"
            class="type-pill"
            class:active={draftPublish === 'draft'}
            onclick={() => (draftPublish = 'draft')}
          >Чернетка</button>
        </div>
      </div>

      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          onclick={resetDraftFilters}
          disabled={draftType === 'all' && draftCategoryIds.length === 0 && draftPublish === 'all'}
        >Скинути</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          onclick={applyFilters}
        >Застосувати</button>
      </div>
    </div>
  {/if}

  {#if confirmExitOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => !savingReorder && (confirmExitOpen = false)}></div>
    <div class="modal-sheet modal-confirm" role="dialog" aria-modal="true" aria-label="Зберегти зміни">
      <div class="modal-head">
        <div class="modal-title">Зберегти новий порядок?</div>
      </div>
      <div class="modal-body">
        <p class="modal-desc">
          Ти змінив порядок розділів. Якщо збережеш — користувачі побачать оновлений каталог.
          Якщо ні — все повернеться як було.
        </p>
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          disabled={savingReorder}
          onclick={revertReorder}
        >Не зберігати</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          disabled={savingReorder}
          onclick={confirmSaveReorder}
        >{savingReorder ? 'Зберігаю…' : 'Зберегти'}</button>
      </div>
    </div>
  {/if}

  {#if matReorderConfirmOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => !matReorderSaving && (matReorderConfirmOpen = false)}></div>
    <div class="modal-sheet modal-confirm" role="dialog" aria-modal="true" aria-label="Зберегти зміни">
      <div class="modal-head">
        <div class="modal-title">Зберегти новий порядок матеріалів?</div>
      </div>
      <div class="modal-body">
        <p class="modal-desc">
          Ти змінив порядок матеріалів у розділі
          <strong>{matReorderCategory?.name ?? ''}</strong>.
          Якщо збережеш — користувачі побачать оновлений порядок. Якщо ні — все повернеться як було.
        </p>
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          disabled={matReorderSaving}
          onclick={revertMatReorder}
        >Не зберігати</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          disabled={matReorderSaving}
          onclick={confirmSaveMatReorder}
        >{matReorderSaving ? 'Зберігаю…' : 'Зберегти'}</button>
      </div>
    </div>
  {/if}

  {#if membersMetric}
    {@const detailWord =
      membersMetric === 'active'
        ? 'активна до'
        : membersMetric === 'ended'
          ? 'закінчилась'
          : membersMetric === 'renewed'
            ? 'продовжила'
            : 'оплата'}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeMembers}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label={membersTitle}>
      <div class="modal-head">
        <div class="modal-title">{membersTitle}{membersTotal ? ` · ${membersTotal}` : ''}</div>
        <button type="button" class="modal-close" onclick={closeMembers} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        {#if membersMetric === 'renewed' && dashboardStats?.renewals.applicable && dashboardStats.renewals.renewedCount > 0}
          <!-- Гроші саме цих продовжень: сума платежів, що закрили ці точки. -->
          <div class="members-sum">
            {uah(dashboardStats.renewals.renewedRevenueCents)}
            <span>· середній чек {uah(dashboardStats.renewals.renewedAvgCents)}</span>
          </div>
        {/if}
        {#if membersLoading && membersItems.length === 0}
          <div class="members-empty">Завантажуємо…</div>
        {:else if membersError}
          <div class="members-empty err">{membersError}</div>
        {:else if membersItems.length === 0}
          <div class="members-empty">Контактів немає</div>
        {:else}
          <div class="members-list">
            {#each membersItems as m (m.telegramId)}
              <div
                class="member-row"
                role="button"
                tabindex="0"
                onclick={() => openContact(m.telegramId)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openContact(m.telegramId)}
              >
                <div class="member-av">
                  {#if m.photoUrl}<img src={m.photoUrl} alt="" />{:else}{(m.fullName?.charAt(0) || '?').toUpperCase()}{/if}
                </div>
                <div class="member-main">
                  <div class="member-name">{m.fullName}</div>
                  <div class="member-sub">
                    {#if m.username}@{m.username}{:else if m.email}{m.email}{:else if m.phone}{m.phone}{:else if m.telegramId.startsWith('-')}контакт з оплати{:else}ID {m.telegramId}{/if}
                  </div>
                </div>
                {#if m.detailAt}
                  <div class="member-detail">{detailWord}<br /><b>{formatDate(m.detailAt)}</b></div>
                {/if}
              </div>
            {/each}
          </div>
          {#if membersHasMore}
            <button type="button" class="members-more" disabled={membersLoading} onclick={() => loadMembers(membersPage + 1)}>
              {membersLoading ? 'Завантажую…' : `Завантажити ще (${membersTotal - membersItems.length})`}
            </button>
          {/if}
        {/if}
      </div>
    </div>
  {/if}

  {#if payStatus}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closePayments}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label={payTitle}>
      <div class="modal-head">
        <div class="modal-title">{payTitle}{payTotal ? ` · ${payTotal}` : ''}</div>
        <button type="button" class="modal-close" onclick={closePayments} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        {#if payLoading && payItems.length === 0}
          <div class="members-empty">Завантажуємо…</div>
        {:else if payError}
          <div class="members-empty err">{payError}</div>
        {:else if payItems.length === 0}
          <div class="members-empty">Оплат немає</div>
        {:else}
          <div class="members-list">
            {#each payItems as p (p.id)}
              <div
                class="member-row"
                role="button"
                tabindex="0"
                onclick={() => openPayment(p.id)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openPayment(p.id)}
              >
                <div class="member-main">
                  <div class="member-name">{p.userName || p.payerName || (p.telegramId ? `ID ${p.telegramId}` : '—')}</div>
                  <div class="member-sub">
                    {#if p.product}{p.product.title}{:else if p.rawProduct}{p.rawProduct}{:else}—{/if} · {formatDate(p.occurredAt)}
                  </div>
                </div>
                <div class="pay-amount" class:refund={p.status === 'refunded'}>
                  {p.status === 'refunded' ? '−' : ''}{uah(p.amountCents)}
                </div>
              </div>
            {/each}
          </div>
          {#if payHasMore}
            <button type="button" class="members-more" disabled={payLoading} onclick={() => loadPayments(payPage + 1)}>
              {payLoading ? 'Завантажую…' : `Завантажити ще (${payTotal - payItems.length})`}
            </button>
          {/if}
        {/if}
      </div>
    </div>
  {/if}

  {#if popularOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closePopular}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Популярні матеріали">
      <div class="modal-head">
        <div class="modal-title">Популярні матеріали{popularTotal ? ` · ${popularTotal}` : ''}</div>
        <button type="button" class="modal-close" onclick={closePopular} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        {#if popularLoading && popularItems.length === 0}
          <div class="members-empty">Завантажуємо…</div>
        {:else if popularError}
          <div class="members-empty err">{popularError}</div>
        {:else if popularItems.length === 0}
          <div class="members-empty">Поки ніхто не додав у вподобані</div>
        {:else}
          <div class="members-list">
            {#each popularItems as m, i (m.id)}
              <div
                class="member-row"
                role="button"
                tabindex="0"
                onclick={() => openMaterial(m)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openMaterial(m)}
              >
                <div class="pop-rank">{i + 1}</div>
                <div class="member-main">
                  <div class="member-name"><Icon name={MATERIAL_TYPE_ICONS[m.type]} size="xs" /> {m.title}</div>
                  {#if m.category}
                    <div class="member-sub"><Icon name={m.category.icon} size="xs" /> {m.category.name}</div>
                  {/if}
                </div>
                <div class="pay-amount"><Icon name="heart" size="xs" filled /> {m.likesCount}</div>
              </div>
            {/each}
          </div>
          {#if popularHasMore}
            <button type="button" class="members-more" disabled={popularLoading} onclick={() => loadPopular(popularPage + 1)}>
              {popularLoading ? 'Завантажую…' : `Завантажити ще (${popularTotal - popularItems.length})`}
            </button>
          {/if}
        {/if}
      </div>
    </div>
  {/if}

  {#if activityDate}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeActivity}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label={activityTitle}>
      <div class="modal-head">
        <div class="modal-title">{activityTitle}{activityTotal ? ` · ${activityTotal}` : ''}</div>
        <button type="button" class="modal-close" onclick={closeActivity} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        {#if activityLoading && activityItems.length === 0}
          <div class="members-empty">Завантажуємо…</div>
        {:else if activityError}
          <div class="members-empty err">{activityError}</div>
        {:else if activityItems.length === 0}
          <div class="members-empty">Нікого не було</div>
        {:else}
          <div class="members-list">
            {#each activityItems as a (a.telegramId)}
              <div
                class="member-row"
                role="button"
                tabindex="0"
                onclick={() => openContact(a.telegramId)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openContact(a.telegramId)}
              >
                <div class="member-av">
                  {#if a.photoUrl}<img src={a.photoUrl} alt="" />{:else}{(a.fullName?.charAt(0) || '?').toUpperCase()}{/if}
                </div>
                <div class="member-main">
                  <div class="member-name">{a.fullName}</div>
                  <div class="member-sub">
                    {#if a.username}@{a.username}{:else if a.email}{a.email}{:else if a.phone}{a.phone}{:else if a.telegramId.startsWith('-')}контакт з оплати{:else}ID {a.telegramId}{/if}
                  </div>
                </div>
                <div class="member-detail">{a.materialsCount} {a.materialsCount === 1 ? 'матеріал' : 'матеріалів'}</div>
              </div>
            {/each}
          </div>
          {#if activityHasMore}
            <button type="button" class="members-more" disabled={activityLoading} onclick={() => loadActivity(activityPage + 1)}>
              {activityLoading ? 'Завантажую…' : `Завантажити ще (${activityTotal - activityItems.length})`}
            </button>
          {/if}
        {/if}
      </div>
    </div>
  {/if}

  {#if tariffModalProductId}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeTariffs}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label={tariffModalTitle}>
      <div class="modal-head">
        <div class="modal-title">
          {tariffModalTitle}{tariffModalRow ? ` · ${uah(tariffModalRow.netCents)}` : ''}
        </div>
        <button type="button" class="modal-close" onclick={closeTariffs} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        {#if !tariffModalRow}
          <div class="members-empty">Завантажуємо…</div>
        {:else if (tariffModalRow.tariffs ?? []).length === 0 && (tariffModalRow.sources ?? []).length === 0}
          <div class="members-empty">Немає розбивки</div>
        {:else}
          <div class="tariff-total">{tariffModalRow.count} оплат</div>
          {#if (tariffModalRow.tariffs ?? []).length > 0}
            <div class="modal-subhead">Тарифи</div>
            <div class="members-list">
              {#each tariffModalRow.tariffs as t (t.tariffId ?? 'none')}
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                <div
                  class="member-row"
                  role="button"
                  tabindex="0"
                  onclick={() => openTariffPayments(t.tariffId, t.title)}
                  onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openTariffPayments(t.tariffId, t.title)}
                >
                  <div class="member-main">
                    <div class="member-name">{t.title}</div>
                    <div class="member-sub">{t.count} оплат</div>
                  </div>
                  <div class="pay-amount">{uah(t.netCents)}</div>
                  <span class="row-chevron" aria-hidden="true">›</span>
                </div>
              {/each}
            </div>
          {/if}
          {#if (tariffModalRow.sources ?? []).length > 0}
            <div class="modal-subhead">Джерела оплати</div>
            <div class="members-list">
              {#each tariffModalRow.sources as s (s.source ?? 'none')}
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                <div
                  class="member-row"
                  role="button"
                  tabindex="0"
                  onclick={() => openSourcePayments(s.source, s.title)}
                  onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openSourcePayments(s.source, s.title)}
                >
                  <div class="member-main">
                    <div class="member-name">{s.title}</div>
                    <div class="member-sub">{s.count} оплат</div>
                  </div>
                  <div class="pay-amount">{uah(s.netCents)}</div>
                  <span class="row-chevron" aria-hidden="true">›</span>
                </div>
              {/each}
            </div>
          {/if}
          {#if (tariffModalRow.utmSources ?? []).length > 0}
            <!-- Окрема вісь від «Джерел оплати» вище: креатив, а не воронка. -->
            <div class="modal-subhead">UTM source</div>
            <div class="members-list">
              {#each tariffModalRow.utmSources ?? [] as s (s.source ?? 'none')}
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                <div
                  class="member-row"
                  role="button"
                  tabindex="0"
                  onclick={() => openUtmSourcePayments(s.source, s.title)}
                  onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && openUtmSourcePayments(s.source, s.title)}
                >
                  <div class="member-main">
                    <div class="member-name">{s.title}</div>
                    <div class="member-sub">{s.count} оплат</div>
                  </div>
                  <div class="pay-amount">{uah(s.netCents)}</div>
                  <span class="row-chevron" aria-hidden="true">›</span>
                </div>
              {/each}
            </div>
          {/if}
        {/if}
      </div>
    </div>
  {/if}

  {#if revenueModal}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeRevenue}></div>
    <div class="modal-sheet revenue-sheet" role="dialog" aria-modal="true" aria-label={revenueTitle}>
      <div class="modal-head">
        <div class="modal-title">{revenueTitle}</div>
        <button type="button" class="modal-close" onclick={closeRevenue} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        {#if revenueLoading && !revenueDetail}
          <div class="members-empty">Рахуємо…</div>
        {:else if revenueError}
          <div class="members-empty err">{revenueError}</div>
        {:else if revenueDetail}
          {@const d = revenueDetail}
          {#if revenueModal === 'gross'}
            <div class="rev-row"><span>Брутто</span><b>{uah(d.grossCents)}</b></div>
            <div class="rev-row"><span>Нетто</span><b>{uah(d.netCents)}</b></div>
            <div class="rev-row">
              <span>Повернення</span>
              <b>{uah(d.refundCents)} · {d.refundCount}{#if d.grossCents > 0} · {((d.refundCents / d.grossCents) * 100).toFixed(2)}%{/if}</b>
            </div>
            <div class="rev-row"><span>Оплат</span><b>{d.count}</b></div>
            <div class="rev-row"><span>Середня виручка / день</span><b>{uah(d.avgPerDayCents)}</b></div>
            <!-- «Повторні оплати», а не «Продовження»: тут розклад каси за датою платежу
                 по ВСІХ продуктах (разом дає брутто), а «Продовження» на плитці — це
                 метрика утримання клубу за датою закінчення доступу. Одне слово на дві
                 різні речі плутало. -->
            <div class="rev-row"><span>Перші покупки / Повторні оплати</span><b>{uah(d.newRevenueCents)} / {uah(d.renewedRevenueCents)}</b></div>

            {#if d.series.length > 1}
              <div class="modal-section-title">
                Виручка по {d.granularity === 'hour' ? 'годинах' : d.granularity === 'day' ? 'днях' : d.granularity === 'week' ? 'тижнях' : 'місяцях'}
              </div>
              {#if revBarSel !== null && d.series[revBarSel]}
                {@const sel = d.series[revBarSel]}
                <div class="rev-bar-info">
                  <b>{sel.range ?? sel.label}</b> · {sel.count} {sel.count === 1 ? 'оплата' : 'оплат'} · <b>{uah(sel.gross)}</b>
                </div>
              {/if}
              <div class="rev-chart">
                <div class="rev-yaxis">
                  <span>{uahK(maxSeries)}</span>
                  <span>{uahK(Math.round(maxSeries / 2))}</span>
                  <span>0</span>
                </div>
                <div class="rev-plotwrap">
                  <div class="rev-plot">
                    <div class="rev-grid"><i></i><i></i><i></i></div>
                    <div class="rev-bars">
                      {#each d.series as day, i (i)}
                        <button
                          type="button"
                          class="rev-bar-col"
                          class:sel={revBarSel === i}
                          title={`${day.label}: ${day.count} · ${uah(day.gross)}`}
                          onclick={() => (revBarSel = revBarSel === i ? null : i)}
                        >
                          <div class="rev-bar" class:on={day.gross > 0} style="height:{Math.max(2, Math.round((day.gross / maxSeries) * 100))}%"></div>
                        </button>
                      {/each}
                    </div>
                  </div>
                  <div class="rev-labels">
                    {#each d.series as day, i (i)}
                      <div class="rev-label">{d.series.length <= 20 || i % Math.ceil(d.series.length / 10) === 0 ? day.label : ''}</div>
                    {/each}
                  </div>
                  {#if d.granularity === 'month' && revYearGroups.length > 1}
                    <div class="rev-years">
                      {#each revYearGroups as g, i (i)}
                        <div class="rev-year" class:divider={i > 0} style="flex:{g.span}">{g.year}</div>
                      {/each}
                    </div>
                  {/if}
                </div>
              </div>
            {/if}
          {:else}
            <div class="rev-row"><span>Середній чек</span><b>{uah(d.avgCents)}</b></div>
            <div class="rev-row"><span>Медіана</span><b>{uah(d.medianCents)}</b></div>
            <div class="rev-row"><span>Мін / Макс</span><b>{uah(d.minCents)} / {uah(d.maxCents)}</b></div>
            <div class="rev-row"><span>Чек перших / повторних</span><b>{uah(d.newAvgCents)} / {uah(d.renewedAvgCents)}</b></div>

            <div class="modal-section-title">Розподіл оплат за сумою</div>
            {#each d.histogram as h (h.label)}
              <div class="hist-row">
                <span class="hist-label">{h.label}</span>
                <div class="hist-track"><div class="hist-fill" style="width:{Math.round((h.count / maxHist) * 100)}%"></div></div>
                <span class="hist-count">{h.count}</span>
              </div>
            {/each}
          {/if}
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .admin-screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 16px 18px;
    color: var(--text-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  }
  .admin-logo-row { display: flex; align-items: center; gap: 10px; }
  .admin-logo-text { font-size: 14px; font-weight: 600; }
  .admin-role { font-size: 10px; opacity: 0.55; margin-top: 2px; }
  .admin-exit {
    font-size: 11px;
    opacity: 0.85;
    cursor: pointer;
    background: rgba(251, 246, 236, 0.1);
    padding: 5px 11px;
    border-radius: 7px;
    color: var(--text-light);
    text-decoration: none;
    transition: opacity 0.15s;
  }
  .admin-exit:hover { opacity: 1; }
  .admin-header-actions { display: flex; align-items: center; gap: 8px; }

  /* Admin bottom nav — visual parity with the main BottomNav (sliding
     indicator + emoji + label). Tabs are tab-state buttons here, not
     route links, but the indexed indicator works the same way. */
  /* Dashboard — period selector + section header link + row amount. */
  .period-row {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }
  .range-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }
  .range-row :global(.dp) { flex: 1; min-width: 0; }
  .dash-product {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 9px 12px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    margin-bottom: 14px;
  }
  .month-nav {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }
  .month-arrow {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid var(--border2);
    background: var(--bg2);
    color: var(--text);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
  }
  .month-arrow:active { background: var(--pu-light); }
  .month-label {
    flex: 1;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
  }

  /* Schedule calendar view */
  .sched-cal { border: 0.5px solid var(--border); border-radius: 12px; padding: 12px; margin-bottom: 14px; }
  .sched-cal-head, .sched-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; }
  .sched-cal-head { margin-bottom: 5px; }
  .sched-hcell { text-align: center; font-size: 10px; color: var(--text-muted); font-weight: 600; }
  .sched-cell {
    aspect-ratio: 1; border: 0.5px solid var(--border); border-radius: 8px; padding: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    background: var(--bg2); color: var(--text); font-family: inherit; gap: 2px;
  }
  .sched-cell.empty { border: none; background: transparent; }
  .sched-cell.has { cursor: pointer; background: var(--pu-light); border-color: var(--outline); }
  .sched-cell.sel { outline: 2px solid var(--pu); outline-offset: 1px; }
  .sched-day { font-size: 11px; font-weight: 600; line-height: 1; }
  .sched-count {
    font-size: 9px; font-weight: 700; color: var(--text-light); background: var(--pu);
    border-radius: 999px; min-width: 14px; text-align: center; line-height: 1.5; padding: 0 3px;
  }
  .cal-hint { font-size: 12px; color: var(--text-muted); text-align: center; padding: 6px 0 2px; }

  .sec-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }
  /* Nudge the activity section down so its gap matches the other sections. */
  .sec-row.activity-row {
    margin-top: 18px;
  }
  .sec-link {
    font-size: 11px;
    font-weight: 600;
    color: var(--accent-text);
    text-decoration: none;
    white-space: nowrap;
  }
  .sec-link.as-button {
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 0;
  }
  .row-amount {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
    flex-shrink: 0;
    margin-left: 8px;
  }

  /* Products tab — filter toggles + system badge. Reuses content-row /
     draft-badge / add-btn / sub-controls from the Content tab. */
  .prod-filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 0 12px;
  }
  .chip-toggle {
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text-muted);
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .chip-toggle.on {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }
  .draft-badge.sys {
    background: var(--pu-light);
    color: var(--accent-text);
  }

  .admin-nav {
    border-top: 0.5px solid var(--border);
    display: flex;
    padding: 8px 0 calc(12px + env(safe-area-inset-bottom));
    flex-shrink: 0;
    background: var(--bg);
    position: relative;
  }
  .admin-nav-indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 20%;
    height: 2px;
    transform: translateX(calc(var(--idx) * 100%));
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
    display: flex;
    justify-content: center;
  }
  .admin-nav-indicator::after {
    content: '';
    width: 40px;
    height: 2px;
    background: var(--pu);
    border-radius: 0 0 2px 2px;
  }
  .admin-nav-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 3px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: inherit;
    font-family: inherit;
  }
  .admin-nav-icon {
    font-size: 17px;
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .admin-nav-item.active .admin-nav-icon {
    transform: scale(1.15) translateY(-1px);
  }
  .admin-nav-label {
    font-size: 9px;
    color: var(--text-muted);
    transition: color 0.2s var(--ease);
  }
  .admin-nav-item.active .admin-nav-label {
    color: var(--accent-text);
    font-weight: 600;
  }

  .admin-body {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
  }
  .admin-body::-webkit-scrollbar { width: 3px; }
  .admin-body::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

  /* Dashboard tab */
  .stat-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 9px;
    margin-bottom: 18px;
  }
  .stat-card {
    background: var(--bg2);
    border-radius: 11px;
    padding: 13px;
    border: 0.5px solid var(--border);
  }
  .stat-card.clickable {
    cursor: pointer;
    transition: border-color 0.15s var(--ease), transform 0.1s var(--ease);
  }
  .stat-card.clickable:hover { border-color: var(--outline); }
  .stat-card.clickable:active { transform: scale(0.985); }

  /* Members drill-in list (inside the shared modal-sheet) */
  .members-list { display: flex; flex-direction: column; gap: 6px; }
  .member-row {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 10px;
    border: 1px solid var(--border);
    border-radius: 11px;
    background: var(--bg);
    cursor: pointer;
    text-align: left;
  }
  .member-row:hover { border-color: var(--outline); }
  .member-av {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
  }
  .member-av img { width: 100%; height: 100%; object-fit: cover; }
  .member-main { flex: 1; min-width: 0; }
  .member-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .member-sub {
    font-size: 11px;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }
  .member-detail {
    flex-shrink: 0;
    font-size: 10px;
    color: var(--text-muted);
    text-align: right;
    line-height: 1.3;
  }
  .member-detail b { color: var(--text); font-size: 11px; }
  .pay-amount { flex-shrink: 0; font-size: 14px; font-weight: 700; color: var(--text); }
  .pay-amount.refund { color: var(--status-inactive); }
  .row-chevron { flex-shrink: 0; color: var(--text-muted); font-size: 20px; line-height: 1; margin-left: 2px; }
  .tariff-total { font-size: 12px; color: var(--text-muted); margin: 0 2px 8px; }
  .modal-subhead {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    margin: 14px 2px 8px;
  }
  .modal-subhead:first-of-type { margin-top: 0; }
  .pop-rank {
    flex-shrink: 0;
    width: 22px;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    color: var(--text-muted);
  }
  .members-empty {
    padding: 28px 10px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
  }
  .members-empty.err { color: var(--status-inactive); }

  /* Сума продовжень над списком контактів у drill-in. */
  .members-sum {
    padding: 8px 12px 10px;
    font-size: 18px;
    font-weight: 700;
    color: var(--accent-text);
    border-bottom: 1px solid var(--bg2);
    margin-bottom: 6px;
  }
  .members-sum span { font-size: 11px; font-weight: 500; color: var(--text-muted); }
  .members-more {
    width: 100%;
    margin-top: 10px;
    padding: 11px;
    border: 1px solid var(--border2);
    border-radius: 10px;
    background: var(--bg);
    color: var(--text);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .members-more:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Revenue detail modal */
  .rev-row {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 9px 2px; border-bottom: 0.5px solid var(--border); font-size: 13px; color: var(--text-muted);
  }
  .rev-row b { color: var(--text); font-size: 14px; font-weight: 700; text-align: right; }
  .rev-chart { display: flex; gap: 6px; margin-top: 4px; }
  .rev-yaxis {
    display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end;
    height: 96px; font-size: 8px; color: var(--text-muted); flex-shrink: 0; line-height: 1;
  }
  .rev-plotwrap { flex: 1; min-width: 0; }
  .rev-plot { position: relative; height: 96px; border-left: 1px solid var(--border2); }
  .rev-grid {
    position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: space-between;
    pointer-events: none;
  }
  .rev-grid i { height: 0; border-top: 0.5px dashed var(--border); }
  .rev-bars { position: absolute; inset: 0; display: flex; align-items: flex-end; gap: 2px; padding-left: 3px; }
  .rev-bar-col {
    flex: 1; height: 100%; display: flex; align-items: flex-end;
    border: none; padding: 0; background: none; cursor: pointer; min-width: 0;
  }
  .rev-bar { width: 100%; border-radius: 3px 3px 0 0; background: var(--pu-light); }
  .rev-bar.on { background: linear-gradient(180deg, var(--pu2), var(--pu)); }
  .rev-bar-col.sel .rev-bar { outline: 2px solid var(--text); outline-offset: 1px; }
  .rev-labels { display: flex; gap: 2px; margin-top: 5px; padding-left: 3px; }
  .rev-label {
    flex: 1; min-width: 0; text-align: center; font-size: 9px; color: var(--text-muted);
    white-space: nowrap; overflow: visible;
  }
  .rev-years { display: flex; margin-top: 3px; padding-left: 3px; }
  .rev-year {
    text-align: center; font-size: 10px; font-weight: 700; color: var(--text-muted);
    padding-top: 4px; min-width: 0;
  }
  .rev-year.divider { border-left: 1px solid var(--border2); }
  .rev-bar-info {
    margin: 6px 0 2px; padding: 8px 10px; border-radius: 8px; background: var(--bg2);
    font-size: 12px; color: var(--text-muted);
  }
  .rev-bar-info b { color: var(--text); font-weight: 700; }
  .hist-row { display: flex; align-items: center; gap: 10px; padding: 6px 2px; }
  .hist-label { width: 88px; flex-shrink: 0; font-size: 12px; color: var(--text-muted); }
  .hist-track { flex: 1; height: 16px; background: var(--bg2); border-radius: 6px; overflow: hidden; }
  .hist-fill { height: 100%; background: linear-gradient(90deg, var(--pu2), var(--pu)); border-radius: 6px; min-width: 2px; }
  .hist-count { width: 34px; flex-shrink: 0; text-align: right; font-size: 12px; font-weight: 700; color: var(--text); }

  .stat-label {
    font-size: 10px;
    color: var(--text-muted);
    margin-bottom: 5px;
    font-weight: 500;
  }
  /* Число + бейдж дельти в один рядок; на вузьких картках бейдж переноситься. */
  .stat-num-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex-wrap: wrap;
  }
  .stat-delta {
    font-size: 10px;
    font-weight: 700;
    line-height: 1.4;
    padding: 1px 5px;
    border-radius: 5px;
    white-space: nowrap;
    cursor: help;
  }
  .stat-delta.up { color: var(--green); background: rgba(92, 109, 90, 0.14); }
  .stat-delta.down { color: var(--status-inactive); background: rgba(168, 80, 47, 0.16); }
  .stat-delta.flat { color: var(--text-muted); background: var(--bg2); }
  .stat-num { font-size: 24px; font-weight: 700; color: var(--text); line-height: 1.1; }
  .stat-num.pu { color: var(--accent-text); }
  .stat-num.green { color: var(--green); }
  .stat-num.orange { color: var(--status-expiring); }
  .stat-num.red { color: var(--status-inactive); }
  .stat-trend {
    font-size: 10px;
    color: var(--green);
    margin-top: 3px;
    font-weight: 500;
  }
  .stat-trend.trend-orange { color: var(--status-expiring); }
  .stat-trend.trend-red { color: var(--status-inactive); }
  .stat-trend.trend-down { color: var(--status-inactive); }

  .sec-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }
  .chart-card {
    border: 0.5px solid var(--border);
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 18px;
  }
  .chart-bar-row {
    display: flex;
    align-items: flex-end;
    gap: 5px;
    height: 60px;
    margin-bottom: 6px;
  }
  .chart-bar {
    flex: 1;
    border-radius: 4px 4px 0 0;
    background: var(--pu-light);
  }
  .chart-bar.active-bar {
    background: linear-gradient(180deg, var(--pu2), var(--pu));
  }
  .chart-bar.clickable { cursor: pointer; }
  .chart-bar.clickable:hover { filter: brightness(1.1); }
  .chart-labels { display: flex; gap: 5px; }
  .chart-label {
    flex: 1;
    font-size: 9px;
    text-align: center;
    color: var(--text-muted);
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;
  }
  .chart-label.clickable { cursor: pointer; color: var(--accent-text); }
  .content-meta-text {
    font-size: 11px;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }


  /* === Subscriptions tab === */
  .sub-controls {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
  }
  .sub-search {
    flex: 1;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 9px 12px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
  }
  .sub-search:focus { border-color: var(--outline); }
  .sub-status {
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 9px 10px;
    font-size: 12px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    cursor: pointer;
  }
  .sub-total {
    font-size: 11px;
    color: var(--text-muted);
    margin-bottom: 10px;
  }
  .sub-placeholder {
    padding: 24px 16px;
    text-align: center;
    border: 1px dashed var(--border2);
    border-radius: 12px;
    background: var(--bg2);
    font-size: 12px;
    color: var(--text-muted);
  }
  .sub-placeholder.error {
    border-color: rgba(168, 80, 47, 0.4);
    color: var(--status-inactive);
  }
  .sub-row {
    display: block;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 8px;
    background: var(--bg2);
    text-decoration: none;
    color: inherit;
    transition: border-color 0.15s var(--ease), transform 0.15s var(--ease);
  }
  .sub-row:active {
    transform: scale(0.99);
    border-color: var(--border2);
  }
  .sub-row-top {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .user-av {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }
  .user-av img { width: 100%; height: 100%; object-fit: cover; }
  .user-text { flex: 1; min-width: 0; }
  .user-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .user-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .badge {
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 10px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .badge-active { background: var(--green-light); color: var(--green); }
  .badge-warn { background: var(--warn-bg); color: var(--status-expiring); }
  .badge-expired { background: var(--danger-bg); color: var(--status-inactive); }
  .badge-muted { background: var(--bg); color: var(--text-muted); border: 1px solid var(--border); }

  .sub-detail {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid var(--border);
  }
  .sub-detail-item {
    font-size: 11px;
    text-align: center;
  }
  .sub-detail-item span {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 2px;
  }
  .sub-detail-item span.date-yellow { color: var(--status-expiring); }
  .sub-detail-item span.date-red { color: var(--status-inactive); }
  .sub-detail-label {
    color: var(--text-muted);
    font-size: 10px;
  }

  .sub-more {
    width: 100%;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--accent-text);
    padding: 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 6px;
    font-family: inherit;
  }
  .sub-more:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Sub-tab switcher inside the Content tab. Visual pill row. */
  .subtab-row {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
    border-bottom: 1px solid var(--border);
    padding-bottom: 10px;
  }
  .subtab {
    flex: 1;
    background: transparent;
    border: 1px solid var(--border2);
    color: var(--text-muted);
    padding: 8px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s var(--ease), color 0.15s var(--ease), border-color 0.15s var(--ease);
  }
  .subtab.active {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }
  /* «Коментарі» — це <a> на окремий роут, а не <button>: додаємо те, що кнопка
     мала за замовчуванням (центрування тексту й знятий підкреслений лінк). */
  a.subtab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-decoration: none;
  }
  /* Червоне коло з кількістю нерозглянутих скарг — сповіщення для адміна. */
  .subtab-badge {
    background: var(--heart);
    color: var(--text-light);
    border-radius: 999px;
    min-width: 17px;
    padding: 1px 5px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
  }

  .add-btn {
    display: block;
    text-align: center;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    padding: 11px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    margin-bottom: 14px;
  }
  .readonly-banner {
    background: var(--pu-light);
    color: var(--accent-text);
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 14px;
  }

  /* === Content tab === */
  /* Filter button replaces the inline type select. Badge shows count of
     active filters (type + category combined). */
  .filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text);
    padding: 9px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.15s var(--ease), background 0.15s var(--ease), color 0.15s var(--ease);
  }
  .filter-btn.has-active {
    border-color: var(--pu);
    color: var(--accent-text);
  }
  .filter-btn.icon-only {
    padding: 9px 11px;
    flex-shrink: 0;
  }
  .reorder-hint {
    flex: 1;
    font-size: 12px;
    color: var(--text-muted);
    padding: 9px 0;
  }
  .reorder-hint-icon {
    color: var(--accent-text);
    font-weight: 700;
    letter-spacing: -2px;
  }
  .reorder-row {
    /* Whole card is the drag target — make sure the cursor reads it as
       grabbable and the browser doesn't try to scroll on touch-drag. */
    cursor: grab;
    touch-action: none;
    user-select: none;
  }
  .reorder-row:active { cursor: grabbing; }
  /* Material reorder: category picker uses button-styled rows */
  .content-row-button {
    width: 100%;
    text-align: left;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 8px;
    font-family: inherit;
    color: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .content-row-button:active { background: var(--bg); }
  .content-chevron {
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
    margin-left: auto;
  }
  /* Блок виручки вище закінчується щільно — свій верхній відступ, щоб розділ
     не злипався з останнім рядком тарифів. */
  .utm-sec-title { margin-top: 22px; }
  .utm-tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
  .utm-tab {
    border: 1px solid var(--border2); background: var(--bg); color: var(--text);
    padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 500;
    cursor: pointer; font-family: inherit;
    transition: background 0.15s var(--ease), color 0.15s var(--ease), border-color 0.15s var(--ease);
  }
  .utm-tab.active { background: var(--pu); color: var(--text-light); border-color: var(--pu); }
  .utm-table {
    border: 0.5px solid var(--border); border-radius: 12px; overflow: hidden;
    background: var(--bg2); margin-bottom: 6px;
  }
  .utm-row {
    display: grid; grid-template-columns: minmax(0, 1.4fr) 0.7fr 1fr 1fr;
    gap: 6px; align-items: center; padding: 10px 12px;
    border-bottom: 0.5px solid var(--border); font-size: 12px;
  }
  .utm-row:last-child { border-bottom: none; }
  .utm-row-head {
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;
    color: var(--text-muted); font-weight: 700;
  }
  .utm-row-total { font-weight: 700; background: var(--bg); }
  .utm-c-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .utm-c-num { text-align: right; font-variant-numeric: tabular-nums; }
  .utm-c-num.strong { font-weight: 600; }
  .utm-c-num small { display: block; font-size: 10px; color: var(--text-muted); font-weight: 400; }
  .utm-note { font-size: 11px; color: var(--text-muted); margin: 0 4px 8px; }
  .filter-badge {
    background: var(--pu);
    color: var(--text-light);
    font-size: 10px;
    padding: 1px 7px;
    border-radius: 999px;
    min-width: 18px;
    text-align: center;
    line-height: 1.4;
  }

  /* Active filter chips — appear below the search row once filters are
     applied. Click anywhere on a chip to drop that single filter. */
  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }
  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--pu-light);
    border: 1px solid var(--pu);
    color: var(--accent-text);
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }
  .filter-chip-x {
    font-size: 11px;
    opacity: 0.75;
    margin-left: 1px;
  }

  /* === Filter modal === */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000;
    pointer-events: auto;
    /* No click handler — per spec the backdrop is non-clickable. The modal
       closes only via the close button or the Apply / Cancel actions. */
  }
  .modal-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 85vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up {
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--text);
  }
  .modal-close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg2);
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-body {
    padding: 14px 18px 18px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }
  /* Extra bottom room so the last content clears the phone's system nav bar. */
  .revenue-sheet .modal-body {
    padding-bottom: calc(40px + env(safe-area-inset-bottom, 0px));
  }
  .modal-section-title {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 10px 0 8px;
  }
  .modal-section-title:first-child { margin-top: 0; }

  .type-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }
  .type-pill {
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text);
    padding: 7px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s var(--ease), color 0.15s var(--ease), border-color 0.15s var(--ease);
  }
  .type-pill.active {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }

  /* Date-range filter row inside the modal — operator dropdown + 1 or 2
     date inputs depending on op. Wrap on narrow screens. */
  .date-filter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .date-op {
    border: 1px solid var(--border2);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    cursor: pointer;
    outline: none;
  }
  .date-input {
    border: 1px solid var(--border2);
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 12px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    min-width: 140px;
  }
  .date-input:focus,
  .date-op:focus { border-color: var(--outline); }

  .modal-actions {
    display: flex;
    gap: 8px;
    padding: 12px 18px calc(16px + env(safe-area-inset-bottom));
    border-top: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-btn {
    flex: 1;
    border: none;
    padding: 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: opacity 0.15s var(--ease);
  }
  .modal-btn-secondary {
    background: var(--bg2);
    color: var(--accent-text);
    border: 1px solid var(--border2);
  }
  .modal-btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .modal-btn-primary {
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
  }

  /* Multi-select category filter — wrapped flex of checkbox chips. Empty
     selection = all. Each chip has a native checkbox so multi-select is
     unambiguous on touch. Once >2 are picked, a clear button appears. */
  .cat-checks {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 4px;
  }
  /* Collapsed: clamp to ~2 chip rows with a fade-out at the bottom so the
     user sees there's more without taking over the screen. Mask is applied
     in addition to overflow:hidden so the cut-off row visibly tapers. */
  .cat-checks.collapsed {
    max-height: 84px;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(to bottom, black 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 55%, transparent 100%);
  }
  .cat-check {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text);
    padding: 7px 11px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s var(--ease), color 0.15s var(--ease), border-color 0.15s var(--ease);
    -webkit-user-select: none;
    user-select: none;
  }
  .cat-check input[type='checkbox'] {
    width: 14px;
    height: 14px;
    accent-color: var(--pu);
    cursor: pointer;
    margin: 0;
  }
  .cat-check.active {
    background: var(--pu-light);
    border-color: var(--pu);
    color: var(--accent-text);
  }
  .cat-check-label { white-space: nowrap; }
  .cat-check-count {
    opacity: 0.7;
    font-size: 11px;
    background: var(--bg2);
    padding: 1px 6px;
    border-radius: 999px;
  }
  .cat-check.active .cat-check-count {
    background: var(--pu);
    color: var(--text-light);
    opacity: 1;
  }
  .cat-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }
  .cat-toggle {
    background: transparent;
    border: none;
    color: var(--accent-text);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 6px 0;
    font-family: inherit;
  }
  .cat-clear {
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--accent-text);
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .content-list { margin-bottom: 8px; }
  .drag-handle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 36px;
    margin: -4px -4px -4px -6px;
    color: var(--text-muted);
    cursor: grab;
    touch-action: none;
    border-radius: 6px;
  }
  .drag-handle:active { cursor: grabbing; background: var(--bg); }
  .content-row.has-handle { gap: 10px; }
  .content-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--bg2);
    margin-bottom: 8px;
    transition: opacity 0.2s var(--ease), transform 0.15s var(--ease), border-color 0.15s var(--ease);
    text-decoration: none;
    color: inherit;
  }
  .content-row.clickable { cursor: pointer; }
  .content-row.clickable:hover { border-color: var(--outline); }

  /* Revenue-by-product card: header + per-tariff breakdown inside one block. */
  .product-block {
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--bg2);
    margin-bottom: 8px;
    overflow: hidden;
  }
  .product-block:hover { border-color: var(--outline); }
  .product-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    cursor: pointer;
  }
  .tariff-inner {
    border-top: 1px solid var(--border);
    padding: 8px 12px 10px 54px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .tariff-inner-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text-muted);
  }
  .tariff-inner-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tariff-inner-count { flex-shrink: 0; }
  .tariff-inner-amount { flex-shrink: 0; font-weight: 600; color: var(--text); }
  .content-row:active {
    transform: scale(0.99);
    border-color: var(--border2);
  }
  .edit-btn {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--bg);
    border: 1px solid var(--border2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s var(--ease);
  }
  .edit-btn:hover { background: var(--pu-light); }
  /* Flip the pencil emoji to point the writing end up-left. */
  .edit-icon {
    display: inline-block;
    transform: scaleX(-1);
  }
  .link-icon {
    display: inline-block;
    font-size: 13px;
  }
  .content-thumb {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: var(--bg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }
  .content-text { flex: 1; min-width: 0; }
  .content-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Only the title truncates; the badge (flex-shrink:0) always stays visible. */
  .content-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .draft-badge {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: var(--warn-bg);
    color: var(--status-expiring);
    padding: 2px 7px;
    border-radius: 999px;
  }
  .draft-badge-warn {
    background: var(--danger-bg);
    color: var(--status-inactive);
  }
  .draft-badge-error {
    background: var(--status-inactive);
    color: var(--text-light);
  }
  .content-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .content-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }
  .content-tag {
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 8px;
    background: var(--pu-light);
    color: var(--accent-text);
    font-weight: 500;
  }

  /* Toggle (matches mockup .toggle). Default = on (purple). */
  .toggle {
    width: 38px;
    height: 22px;
    border-radius: 12px;
    background: var(--pu);
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s var(--ease);
    border: none;
    padding: 0;
  }
  .toggle:disabled { cursor: not-allowed; opacity: 0.65; }
  .toggle-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--bg);
    position: absolute;
    top: 2px;
    left: 18px;
    transition: left 0.2s var(--ease);
  }
  .toggle.off { background: var(--border2); }
  .toggle.off .toggle-dot { left: 2px; }
</style>
